import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAppNavigate } from "utils/router";
import { useSearchParams, useLocation } from "react-router-dom";
import JSZip from 'jszip';
import {
  ArrowLeft,
  ArrowRight,
  DollarSign,
  CreditCard,
  AlertTriangle,
  Sparkles,
  CheckCircle2,
  Upload,
  Zap,
  Building2,
  DatabaseZap,
  TrendingUp,
  Target,
  Download,
  Bell,
  Goal,
  CheckCircle,
  ExternalLink,
  Save,
  Database,
  Clock,
  UserPlus,
  Gauge,
  ChevronDown,
  ChevronRight,
  X,
  BookOpen,
  Plus,
  Settings,
  Trash2,
  Activity,
  Loader2
} from 'lucide-react';
import { SetupRevenueAlerts } from './SetupRevenueAlerts';
import { SetupHeader } from '../Common/SetupHeader';
import { RevenueConnectionsList } from './RevenueConnectionsList';
import { EnvironmentSelector } from './Elements/EnvironmentSelection';
import { TeamId } from "store/redux/reducers/team/teamConstants";
import CommingSoonComponent from "../../Build/common/CommingSoonComponent"
import DataCollectionMethod from './DataCollectionMethod';
import DataSetup from './DataSetup';
import SetupMRR from './CoreRevenueSetting';
import SetupARR from './AdvanceRevenueSetting';
import { GoalSetupModal } from './SetGoals';
import {
  type Goals,
  buildUpsertRevenueGoalInputs,
  hasAnySetGoalEnabled,
  isSetGoalsStepCompleted,
} from "./utils/setGoalsStepUtils";
import { useSelector, useDispatch, useStore } from "react-redux";
import { RootState } from "store/redux/store";
import { getProductId, getEnvironmentId, setHideEnvironmentSelector, getUser, getAccountId } from "utils/localStorage";
import { updateStepStatus } from "store/redux/reducers/moduleStep/moduleStepSlice";
import { useStoreCustomerAnalyticsOnboardingDetailsMutation } from "store/graphql/codegen/auto-gen/onboarding";
import { onboardingClient, plgCRMClient, revenueClient } from "store/graphql/apollo/clients";
import PlannedPill from 'components/Shared/UI/PlannedPill/PlannedPill';
import { useDemoMode } from '../../../context/DemoModeContext';
import { useGetCaOnboardingStepDetailsLazyQuery } from "../../../store/graphql/codegen/auto-gen/onboarding";
import { useUploadCsvMutation, useDeleteAirbyteIntegrationMutation, useUpsertRevenueGoalTargetsMutation, IntegrationType } from "../../../store/graphql/codegen/auto-gen/revenue-report";
import { setField as setRevenueAnalyticsField, patchState as patchRevenueAnalyticsState, hydrateFromServer, setStep2FileInfo, setStep2IsProcessingAt, RevenueAnalyticsSliceState } from "store/redux/reducers/revenueAnalytics/revenueAnalyticsSlice";
import { step2SampleRows, downloadStep2Sample, generateSampleDataForDownload, SeededRandom } from '../../../data/demoData/UseCases/RevenueAnalytics/sampleData'
import { step2Configs, FieldNameMapping, getRequiredSubsteps, paymentTransactionsFields, FieldNameMappingPaymentTransactions, reverseTransformStep4Data, invoicesFields, FieldNameMappingInvoices } from './utils/utils'
import { InviteTeamModal } from "../Common/InviteTeamModal";
import { extractCSVHeaders, extractCSVStatuses } from '../../UseCases/RevenueAnalytics/utils/csvProcessor'
import { DataSource, DataModelType } from "../../../store/graphql/codegen/auto-gen/revenue-report";
import { stepsMap, stepsEventNameMap, getEntityTypeForSubstep, transformStep4Data, dataModelType } from './utils/utils'
import { API_ENDPOINTS } from '../../../constants/http-endpoints'
import Cookies from 'js-cookie';
import DatasetSchemaModal from './DataSetSchemaModal';
import { TRANSACTION_FIELDS, INVOICE_FIELDS } from "./utils/constants"
import SchemaDrawer from './SchemaDrawer';
import { SiStripe, SiQuickbooks } from 'react-icons/si';
import ChargebeeIcon from '../../../assets/images/Chargebee_ideEpxhfdE_1.svg';
import { useOnboardingContext } from "../../../container/Layout/OnboardingContext/OnboardingContext";
import {
  NotificationPreferencesModal,
  useNotificationPrefsUrlParam
} from "components/Shared/UI/NotificationPreferences";

const REVENUE_NOTIFICATION_KEY = 'rule_revenue_setup_telemetry';
import DiagnosticIssueCards from '../../DiagnosticsReport/DiagnosticIssueCards';
import RevenueMissingDataCards from '../../DiagnosticsReport/RevenueMissingDataCards';
import { useGetUserAndGroupTrackingStatsLazyQuery, useExportUserAndGroupTrackingStatsLazyQuery, EntityType as PlgEntityType } from "../../../store/graphql/codegen/auto-gen/plg-dashboard";
import { useGetRevenueCustomerAndAccountStatsLazyQuery } from "../../../store/graphql/codegen/auto-gen/revenue-report";
import { SetupGuide } from './Stripe/components/SetupGuide';
import { ChargebeeSetupGuide } from './Chargebee/components/ChargebeeSetupGuide';
import SetupHealthStatusGuide from '../../DiagnosticsReport/SetupHealthStatusGuide';
import { SETUP_HEALTH_ISSUE_IDS, SETUP_HEALTH_ISSUE_LABELS, getConfigureAccountIdentifierLabel } from '../../../constants/setupHealthIssues';


const customerToken = Cookies.get("system_token");

export type EmailManager = ReturnType<typeof useEmailManager>

interface EmailManagerState {
    emails: string[];
    inputValue: string;
    error: string;
    showInput: boolean;
    showUpdateButton: boolean;
}

export const useEmailManager = (initialEmails: string[] = []) => {
    const [state, setState] = useState<EmailManagerState>({
        emails: initialEmails,
        inputValue: '',
        error: '',
        showInput: initialEmails.length > 0,
        showUpdateButton: false
    });

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ',' || e.key === 'Enter') {
            e.preventDefault();
            const email = state.inputValue.trim();

            if (!email) return;

            if (!validateEmail(email)) {
                setState(prev => ({ ...prev, error: 'Please enter a valid email address' }));
                return;
            }

            if (state.emails.includes(email)) {
                setState(prev => ({ ...prev, error: 'This email has already been added' }));
                return;
            }

            setState(prev => ({
                ...prev,
                emails: [...prev.emails, email],
                inputValue: '',
                error: '',
                showUpdateButton: true
            }));
        }
    };

    const handleInputChange = (value: string) => {
        setState(prev => ({
            ...prev,
            inputValue: value,
            error: prev.error ? '' : prev.error
        }));
    };

    const removeEmail = (emailToRemove: string) => {
        setState(prev => ({
            ...prev,
            emails: prev.emails.filter(email => email !== emailToRemove),
            showUpdateButton: prev.emails.length > 1
        }));
    };

    const toggleInput = () => {
        setState(prev => ({ ...prev, showInput: !prev.showInput }));
    };

    const resetUpdateButton = () => {
        setState(prev => ({ ...prev, showUpdateButton: false }));
    };

    const setEmails = (emails: string[]) => {
        setState(prev => ({
            ...prev,
            emails,
            showInput: emails.length > 0
        }));
    };

    return {
        state,
        handleKeyDown,
        handleInputChange,
        removeEmail,
        toggleInput,
        resetUpdateButton,
        setEmails
    };
};

const useRevenueAnalyticsField = <K extends keyof RevenueAnalyticsSliceState>(key: K) => {
  const dispatch = useDispatch();
  const value = useSelector((state: RootState) => state.revenueAnalytics[key]);
  const setter = useCallback(
    (nextValue: RevenueAnalyticsSliceState[K] | ((prev: RevenueAnalyticsSliceState[K]) => RevenueAnalyticsSliceState[K])) => {
      const resolved =
        typeof nextValue === "function"
          ? (nextValue as (prev: RevenueAnalyticsSliceState[K]) => RevenueAnalyticsSliceState[K])(value)
          : nextValue;
      dispatch(setRevenueAnalyticsField({ key, value: resolved }));
    },
    [dispatch, key, value]
  );
  return [value, setter] as const;
};

function RevenueOptimization() {

  document.title = "Revenue Intelligence Setup";

  const revenueNotificationPrefs = useNotificationPrefsUrlParam();
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleState = useSelector((state: RootState) => state.modules);

  const [currentStep, setCurrentStep] = useRevenueAnalyticsField("currentStep");
  const [successSteps, setSuccessSteps] = useRevenueAnalyticsField("successSteps");
  const [demoModeSuccessSteps, setDemoModeSuccessSteps] = useRevenueAnalyticsField("demoModeSuccessSteps");


  const [selectedSources, setSelectedSources] = useRevenueAnalyticsField("selectedSources");
  const [selectedDataType, setSelectedDataType] = useRevenueAnalyticsField("selectedDataType");
  const [activeSourceTab, setActiveSourceTab] = useRevenueAnalyticsField("activeSourceTab");
  const [sourceConfigurations, setSourceConfigurations] = useRevenueAnalyticsField("sourceConfigurations");

  const [processing, setProcessing] = useRevenueAnalyticsField("processing");
  const [saveError, setSaveError] = useRevenueAnalyticsField("saveError");
  const { toggleDemoMode, isDemoMode } = useDemoMode()
  const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false);
  const [isSchemaDrawerOpen, setIsSchemaDrawerOpen] = useState(false);
  const [modalTransition, setModalTransition] = useState<'none' | 'drawer-to-modal' | 'modal-to-drawer'>('none');
  const [originalApiKeyData, setOriginalApiKeyData] = useRevenueAnalyticsField("originalApiKeyData");
  const { } = useOnboardingContext();

  const [healthCheckCompletedStates, setHealthCheckCompletedStates] = useRevenueAnalyticsField("healthCheckCompletedStates");
  const [, setIsLoadingRevenueStats] = useState(false);

  // Demo-only: integration list view and connections state (persisted in localStorage)
  const DEMO_CONNECTIONS_STORAGE_KEY = "revenue_analytics_demo_connections";
  type DemoViewMode = "list" | "setup";
  const [viewMode, setViewMode] = useState<DemoViewMode>("setup");
  const [isNewConnectionMode, setIsNewConnectionMode] = useState(false);
  const [connectionDraftKey, setConnectionDraftKey] = useState(0);
  const [demoConnections, setDemoConnections] = useState<Array<{
    id: string;
    type: string;
    name: string;
    lastSync?: string;
    coreSummary?: string;
    advancedSummary?: string;
    sourceEnvironment?: string;
    targetEnvironment?: string;
    accountIdLabel?: string;
    accountIdValue?: string;
    syncStartDate?: string;
    accountIdentifierKey?: string;
    sourceConfig?: Record<string, unknown>;
  }>>([]);
  const [editingConnectionId, setEditingConnectionId] = useState<string | null>(null);
  const [deleteModalConnection, setDeleteModalConnection] = useState<{ id: string; type: string; name: string; sourceConfig?: Record<string, unknown> } | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const lastTrackedPayloadRef = useRef<{ signature: string; timestamp: number } | null>(null);

  type ListConnection = {
    id: string;
    type: string;
    name: string;
    lastSync?: string;
    sourceEnvironment?: string;
    targetEnvironment?: string;
    accountIdLabel?: string;
    accountIdValue?: string;
    syncStartDate?: string;
    accountIdentifierKey?: string;
    sourceConfig?: Record<string, unknown>;
    sourceCategory?: string;
  };

  const persistDemoConnections = useCallback((connections: typeof demoConnections) => {
    try {
      localStorage.setItem(DEMO_CONNECTIONS_STORAGE_KEY, JSON.stringify(connections));
    } catch (_) {}
  }, []);

  const loadDemoConnections = useCallback(() => {
    try {
      const raw = localStorage.getItem(DEMO_CONNECTIONS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setDemoConnections(parsed);
      }
    } catch (_) {}
  }, []);

  const openSchemaModal = () => {
    if (isSchemaDrawerOpen) {
      setModalTransition('drawer-to-modal');
      setIsSchemaDrawerOpen(false);
      setTimeout(() => {
        setIsSchemaModalOpen(true);
        setModalTransition('none');
      }, 150);
    } else {
      setIsSchemaModalOpen(true);
    }
  };

  const openSchemaDrawer = () => {
    if (isSchemaModalOpen) {
      setModalTransition('modal-to-drawer');
      setIsSchemaModalOpen(false);
      setTimeout(() => {
        setIsSchemaDrawerOpen(true);
        setModalTransition('none');
      }, 150);
    } else {
      setIsSchemaDrawerOpen(true);
    }
  };

  const closeAllModals = () => {
    setIsSchemaModalOpen(false);
    setIsSchemaDrawerOpen(false);
    setModalTransition('none');
  };

  const dataModelRef = useRef<HTMLDivElement>(null);

  const [invoiceSettings, setInvoiceSettings] = useState({
    newSubscriberTrigger: "fully_paid",
    existingSubscriberTrigger: "issued",
    preventInvoiceConditions: { writtenOff: true, voided: true, fullyRefunded: false },
    pastDueHandling: "remain_past_due",
    autoCancelDays: 14
  });

  
  const [UserAndGroupTracking, setUserAndGroupTracking] = useState<any>(null);
  const [revenueStats, setRevenueStats] = useState<{
    totalCustomers: number;
    customerWithMissingIdentifier: number;
    totalAccountIdentifiers: number;
    untrackedAccountIdentifiers: number;
    totalGroups: number;
    transactionsWithoutInvoice: number;
  } | null>(null);
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const [expandedRevenueMissingIssueId, setExpandedRevenueMissingIssueId] = useState<string | null>(null);
  const [expandedAccountIdentifierSection, setExpandedAccountIdentifierSection] = useState(false);
  const [accountIdentifierForHealthStatus, setAccountIdentifierForHealthStatus] = useState("group_id");
  const [isSetupGuideOpen, setIsSetupGuideOpen] = useState(false);
  const [setupGuideSource, setSetupGuideSource] = useState<'stripe' | 'chargebee' | null>(null);
  const [expanded, setExpanded] = useState(false);
  const setupGuideStep3Ref = useRef<HTMLDivElement>(null);
  const openSetupGuideRef = useRef<(() => void) | null>(null);
  const [isHealthStatusGuideOpen, setIsHealthStatusGuideOpen] = useState(false);

  
  const [GetUserAndGroupTrackingStats] = useGetUserAndGroupTrackingStatsLazyQuery({
    client: plgCRMClient,
    fetchPolicy: "no-cache"
  });

  const [getRevenueCustomerAndAccountStats] = useGetRevenueCustomerAndAccountStatsLazyQuery({
    client: revenueClient,
    fetchPolicy: "no-cache"
  });

  const [ExportUserAndGroupTrackingStats] = useExportUserAndGroupTrackingStatsLazyQuery({
    client: plgCRMClient,
    fetchPolicy: "no-cache"
  });

  
  const getIssueCountsFromData = useCallback(() => {
    if (isDemoMode) {
      const totalGroups = 1536;
      const untrackedGroups = 328;
      const totalAccountIdentifiers = 592;
      const customerWithMissingIdentifier = 20;
      const untrackedAccountIdentifiers = 50;

      const identifiedWithRevenue = Math.max(totalAccountIdentifiers - untrackedAccountIdentifiers, 0);
      const noAccountIdentifiers = customerWithMissingIdentifier;
      const trackedInProduct = totalGroups - untrackedGroups;
      const trackedInRevenue = totalAccountIdentifiers - untrackedAccountIdentifiers;
      const identifiedNoRevenue = Math.max(trackedInProduct - trackedInRevenue, 0);
      const unidentifiedFromRevenue = untrackedAccountIdentifiers;
      const unidentifiedFromProduct = untrackedGroups;
      const unidentifiedTotal = unidentifiedFromRevenue + unidentifiedFromProduct;

      return {
        idMissingAccountsCount: noAccountIdentifiers,
        missingRevenueDataCount: identifiedNoRevenue,
        unidentifiedAccountsCount: unidentifiedTotal,
        unidentifiedFromRevenueCount: unidentifiedFromRevenue,
        unidentifiedFromProductCount: unidentifiedFromProduct,
      };
    }

    
    const totalGroups = UserAndGroupTracking?.totalGroups || 0;
    const untrackedGroups = UserAndGroupTracking?.untrackedGroups || 0;
    const totalAccountIdentifiers = revenueStats?.totalAccountIdentifiers || 0;
    const customerWithMissingIdentifier = revenueStats?.customerWithMissingIdentifier || 0;
    const untrackedAccountIdentifiers = revenueStats?.untrackedAccountIdentifiers || 0;

    
    const identifiedWithRevenue = Math.max(totalAccountIdentifiers - untrackedAccountIdentifiers, 0);
    const noAccountIdentifiers = customerWithMissingIdentifier;
    const trackedInProduct = totalGroups - untrackedGroups;
    const trackedInRevenue = totalAccountIdentifiers - untrackedAccountIdentifiers;
    const identifiedNoRevenue = Math.max(trackedInProduct - trackedInRevenue, 0);
    const unidentifiedFromRevenue = untrackedAccountIdentifiers;
    const unidentifiedFromProduct = untrackedGroups;
    const unidentifiedTotal = unidentifiedFromRevenue + unidentifiedFromProduct;

    return {
      idMissingAccountsCount: noAccountIdentifiers,
      missingRevenueDataCount: identifiedNoRevenue,
      unidentifiedAccountsCount: unidentifiedTotal,
      unidentifiedFromRevenueCount: unidentifiedFromRevenue,
      unidentifiedFromProductCount: unidentifiedFromProduct,
    };
  }, [isDemoMode, UserAndGroupTracking, revenueStats]);

  const {
    idMissingAccountsCount,
    missingRevenueDataCount,
    unidentifiedAccountsCount,
    unidentifiedFromRevenueCount,
    unidentifiedFromProductCount,
  } = getIssueCountsFromData();

  const transactionsWithoutInvoiceCount = revenueStats?.transactionsWithoutInvoice ?? 0;


  
  useEffect(() => {
    if (sourceConfigurations.stripe?.selectedIdentifierKey) {
      setAccountIdentifierForHealthStatus(sourceConfigurations.stripe.selectedIdentifierKey);
    } else {
      setAccountIdentifierForHealthStatus('group_id');
    }
  }, [sourceConfigurations.stripe?.selectedIdentifierKey]);

  
  useEffect(() => {
    if (currentStep === 7 && !isDemoMode) {
      setIsLoadingRevenueStats(true);
      GetUserAndGroupTrackingStats({
        variables: {
          input: {
            environmentId: getEnvironmentId() || ""
          }
        },
        onCompleted: (data) => {
          setUserAndGroupTracking(data?.getUserAndGroupTrackingStats);
        },
        onError: (error) => {
          console.error("Error fetching user and group tracking stats:", error);
        }
      });

      getRevenueCustomerAndAccountStats({
        variables: {
          input: {
            environmentId: getEnvironmentId() || ""
          }
        },
        onCompleted: (data) => {
          if (data?.getRevenueCustomerAndAccountStats) {
            setRevenueStats({
              totalCustomers: data.getRevenueCustomerAndAccountStats.totalCustomers,
              customerWithMissingIdentifier: data.getRevenueCustomerAndAccountStats.customerWithMissingIdentifier,
              totalAccountIdentifiers: data.getRevenueCustomerAndAccountStats.totalAccountIdentifiers,
              untrackedAccountIdentifiers: data.getRevenueCustomerAndAccountStats.untrackedAccountIdentifiers,
              totalGroups: data.getRevenueCustomerAndAccountStats.totalGroups,
              transactionsWithoutInvoice: data.getRevenueCustomerAndAccountStats.transactionsWithoutInvoice
            });
          }
          setIsLoadingRevenueStats(false);
        },
        onError: (error) => {
          console.error("Error fetching revenue stats:", error);
          setIsLoadingRevenueStats(false);
        }
      });
    }
  }, [currentStep, isDemoMode, GetUserAndGroupTrackingStats, getRevenueCustomerAndAccountStats]);

  const requiresDataTypeSelection = selectedSources.some(
    source => source === 'csv' || source === 'api'
  );

  const scrollToDataModel = () => {
    const scrollContainer = document.querySelector('.flex-1.overflow-auto');
    let targetElement = null;

    if (dataModelRef.current) {
      targetElement = dataModelRef.current;
    } else {
      targetElement = document.getElementById('select-data-model-section');
    }

    if (!targetElement || !scrollContainer) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = targetElement.getBoundingClientRect();
    const offset = 80;

    const relativeTop = elementRect.top - containerRect.top;
    const scrollTop = scrollContainer.scrollTop + relativeTop - offset;

    scrollContainer.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    setHideEnvironmentSelector('true');

    window.dispatchEvent(new CustomEvent('environmentSelectorVisibilityChange', {
      detail: { hidden: true }
    }));

    const handlePageHide = () => {
      setHideEnvironmentSelector('false');
    };

    window.addEventListener('beforeunload', handlePageHide);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      setHideEnvironmentSelector('false');

      window.dispatchEvent(new CustomEvent('environmentSelectorVisibilityChange', {
        detail: { hidden: false }
      }));

      window.removeEventListener('beforeunload', handlePageHide);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  useEffect(() => {
    if (selectedSources.length > 0) {
      const timer = setTimeout(() => {
        scrollToDataModel();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [selectedSources, requiresDataTypeSelection]);

  const dataCollectionOptions = [
    {
      id: 'stripe',
      title: 'Stripe Integration',
      description: 'Automatically sync payment data from your Stripe account',
      icon: SiStripe,
      color: 'blue',
      status: 'available',
      features: ['Auto-sync payments', 'Customer data', 'Subscription tracking', 'Real-time updates']
    },
    {
      id: 'chargebee',
      title: 'Chargebee Integration',
      description: 'Automatically sync subscription and billing data from Chargebee',
      icon: ChargebeeIcon,
      color: 'orange',
      status: 'available',
      features: ['Auto-sync subscriptions', 'Customer data', 'Invoice tracking', 'Real-time updates']
    },
    {
      id: 'api',
      title: 'API Integration',
      description: 'Send single or bulk records via our REST API for real-time sync',
      icon: Zap,
      color: 'blue',
      status: 'available',
      features: ['Real-time sync', 'Bulk operations', 'Webhook support', 'Authentication']
    },
    {
      id: 'csv',
      title: 'Manual CSV Upload',
      description: 'Upload your revenue data in CSV format for one-time import',
      icon: Upload,
      color: 'blue',
      status: 'available',
      badge: 'beta',
      features: ['One-time import', 'Historical data', 'Bulk processing', 'Data validation']
    },
    {
      id: 'salesforce',
      title: 'Salesforce Integration',
      description: 'Import sales data directly from your Salesforce CRM',
      icon: Building2,
      color: 'orange',
      status: 'coming-soon',
      features: ['CRM integration', 'Lead tracking', 'Opportunity sync', 'Custom fields']
    },
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'border-blue-200 hover:border-blue-300',
      green: 'border-green-200 hover:border-green-300',
      purple: 'border-purple-200 hover:border-purple-300',
      orange: 'border-orange-200 hover:border-orange-300'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const getIconColor = (color: string, isDisabled: boolean = false) => {
    if (isDisabled) return 'text-gray-400';
    const colors = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      orange: 'text-orange-600'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const [expandedStep2SubStep, setExpandedStep2SubStep] = useRevenueAnalyticsField("expandedStep2SubStep");
  const [step2ImportResults, setStep2ImportResults] = useRevenueAnalyticsField("step2ImportResults");
  const [step2UploadedFiles, setStep2UploadedFiles] = useState<(File | null)[]>(Array.from({ length: step2Configs.length }, () => null));
  const [step2FieldMappings, setStep2FieldMappings] = useRevenueAnalyticsField("step2FieldMappings");
  const [step2CsvHeaders, setStep2CsvHeaders] = useRevenueAnalyticsField("step2CsvHeaders");
  const [step2IsProcessing, setStep2IsProcessing] = useRevenueAnalyticsField("step2IsProcessing");
  const [step2SubStepStatuses, setStep2SubStepStatuses] = useRevenueAnalyticsField("step2SubStepStatuses");
  const mapFieldsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const nextButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedMrrPlans, setSelectedMrrPlans] = useRevenueAnalyticsField("selectedMrrPlans");
  const [selectedMrrStatuses, setSelectedMrrStatuses] = useRevenueAnalyticsField("selectedMrrStatuses");
  const [selectedRevenueOptions, setSelectedRevenueOptions] = useRevenueAnalyticsField("selectedRevenueOptions");
  const [selectedChurnOptions, setSelectedChurnOptions] = useRevenueAnalyticsField("selectedChurnOptions");
  const [isReady, setIsReady] = useState(false);

  const [selectedTeam, setSelectedTeam] = useState<TeamId | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedPastDueHandling, setSelectedPastDueHandling] = useRevenueAnalyticsField("selectedPastDueHandling");
  const [csvDetectedStatuses, setCsvDetectedStatuses] = useRevenueAnalyticsField("csvDetectedStatuses");
  const [isMrrCompleted, setIsMrrCompleted] = useRevenueAnalyticsField("isMrrCompleted");
  const [includeTax, setIncludeTax] = useRevenueAnalyticsField("includeTax");
  const [baseCurrency, setBaseCurrency] = useRevenueAnalyticsField("baseCurrency");
  const [includeTransactionFees, setIncludeTransactionFees] = useRevenueAnalyticsField("includeTransactionFees");
  const [chargebeeFeePerTransaction, setChargebeeFeePerTransaction] = useRevenueAnalyticsField("chargebeeFeePerTransaction");
  const [paymentGatewayFees, setPaymentGatewayFees] = useRevenueAnalyticsField("paymentGatewayFees");
  const [paymentGatewayType, setPaymentGatewayType] = useRevenueAnalyticsField("paymentGatewayType");
  const [chargebeeChargesPerTxnFee, setChargebeeChargesPerTxnFee] = useRevenueAnalyticsField("chargebeeChargesPerTxnFee");
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useRevenueAnalyticsField("selectedPaymentGateway");

  const emailManager = useEmailManager();

  const [selectedApiKeyData, setSelectedApiKeyData] = useRevenueAnalyticsField("selectedApiKeyData");
  const [apiKeyChanged, setApiKeyChanged] = useRevenueAnalyticsField("apiKeyChanged");
  const [apiKeyMarkedComplete, setApiKeyMarkedComplete] = useRevenueAnalyticsField("apiKeyMarkedComplete");

  
  const [goals, setGoals] = useRevenueAnalyticsField("goals");
  const [step5LiveGoals, setStep5LiveGoals] = useState<Goals | null>(null);
  const [step5NavBusy, setStep5NavBusy] = useState(false);

  const steps = [
    { id: 1, title: 'Data Source', icon: <DatabaseZap className="size-5" /> },
    { id: 2, title: 'Data Setup', icon: <Upload className="size-5" /> },
    { id: 3, title: 'Core Revenue Calculation', icon: <TrendingUp className="size-5" /> },
    { id: 4, title: 'Advance Revenue Calculation', icon: <Target className="size-5" /> },
    { id: 5, title: 'Set Goals', icon: <Goal className="size-5" /> },
    { id: 6, title: 'Revenue Alerts', icon: <Bell className="size-5" />, comingSoon: true },
    { id: 7, title: 'Setup Health Status', icon: <Gauge className="size-5" /> }
  ];

  const getValidStepFromUrl = useCallback((): number | null => {
    const stepParam = searchParams.get('step');
    const stateParam = searchParams.get('state');

    let step: number | null = null;

    if (stateParam) {
      try {
        const state = JSON.parse(decodeURIComponent(stateParam));
        if (state.step) {
          step = parseInt(state.step, 10);
        }
      } catch (e) {
        console.error("Invalid state param:", e);
      }
    }

    if (!step && stepParam) {
      const parsedStep = parseInt(stepParam, 10);
      if (!isNaN(parsedStep)) step = parsedStep;
    }

    if (!step || step < 1 || step > steps.length) return null;

    return step;
  }, [searchParams, steps.length]);


  const updateUrlWithStep = useCallback((step: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    newSearchParams.delete('code');
    newSearchParams.delete('state');
    
    newSearchParams.set('step', step.toString());
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

const cleanOAuthParamsFromUrl = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete('code');
    newSearchParams.delete('state');
    setSearchParams(newSearchParams, { replace: true });
}, [searchParams, setSearchParams]);

  const cleanUrlStep = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('step');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const cleanUrlStepAndSubstep = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('step');
    newSearchParams.delete('substep');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const isStepAccessibleFromCompletedSteps = useCallback((stepId: number, completedSteps: number[]): boolean => {
    if (stepId === 1) return true;

    for (let i = 1; i < stepId; i++) {
      if (!completedSteps.includes(i)) {
        return false;
      }
    }
    return true;
  }, []);

  const [GetCaOnboardingStepDetails] = useGetCaOnboardingStepDetailsLazyQuery({
    client: onboardingClient,
    fetchPolicy: "no-cache"
  });

  const [storeCustomerAnalyticsOnboarding] = useStoreCustomerAnalyticsOnboardingDetailsMutation({
    client: onboardingClient,
    fetchPolicy: "no-cache"
  });

  const [deleteAirbyteIntegration, { loading: deleteIntegrationLoading }] = useDeleteAirbyteIntegrationMutation({
    client: revenueClient,
    fetchPolicy: "no-cache"
  });

  const [upsertRevenueGoalTargetsForStep5] = useUpsertRevenueGoalTargetsMutation({
    client: revenueClient,
    fetchPolicy: "no-cache",
  });

  const handleStep5GoalsChange = useCallback((g: Goals) => {
    setStep5LiveGoals(g);
  }, []);

  const commitSetGoalsOnboardingState = useCallback(
    async (g: Goals) => {
      if (isDemoMode) return;
      const isCompleted = isSetGoalsStepCompleted(g);
      const result = await storeCustomerAnalyticsOnboarding({
        variables: {
          input: {
            environmentId: getEnvironmentId() ?? "",
            productId: getProductId() ?? "",
            moduleId: "revenue_analytics",
            stepId: "set_goals",
            isCompleted,
            data: JSON.stringify({ goals: g }),
          },
        },
      });
      if (!result.data?.storeCustomerAnalyticsOnboardingDetails?.isSuccess) {
        throw new Error("Failed to save onboarding step");
      }
      dispatch(
        updateStepStatus({
          path: "revenue_analytics.set_goals",
          value: JSON.stringify({ goals: g }),
          isCompleted,
        })
      );
      if (isCompleted) {
        setSuccessSteps((prev) => (prev.includes(5) ? prev : [...prev, 5]));
      }
      setGoals(g);
    },
    [isDemoMode, storeCustomerAnalyticsOnboarding, dispatch, setSuccessSteps, setGoals]
  );

  const persistStep5GoalsAndOnboarding = useCallback(
    async (g: Goals) => {
      if (isDemoMode) return;
      const upsertRes = await upsertRevenueGoalTargetsForStep5({
        variables: {
          input: {
            goals: buildUpsertRevenueGoalInputs(g),
            environmentId: getEnvironmentId() ?? "",
            productId: getProductId() ?? "",
          },
        },
      });
      if (upsertRes.errors?.length) {
        throw new Error(upsertRes.errors[0]?.message ?? "Failed to save goals");
      }
      await commitSetGoalsOnboardingState(g);
    },
    [isDemoMode, upsertRevenueGoalTargetsForStep5, commitSetGoalsOnboardingState]
  );

  const handleStep5GoToIntegrations = useCallback(async () => {
    if (isDemoMode) {
      setViewMode("list");
      return;
    }
    const g = step5LiveGoals;
    if (!g || !hasAnySetGoalEnabled(g)) {
      setViewMode("list");
      return;
    }
    if (!isSetGoalsStepCompleted(g)) return;
    setStep5NavBusy(true);
    setSaveError(null);
    try {
      await persistStep5GoalsAndOnboarding(g);
      setViewMode("list");
    } catch {
      setSaveError("Something went wrong. Try again later.");
    } finally {
      setStep5NavBusy(false);
    }
  }, [isDemoMode, step5LiveGoals, persistStep5GoalsAndOnboarding, setSaveError]);

  const handleStep5GoToDashboard = useCallback(async () => {
    if (isDemoMode) {
      navigate("/revenue-analytics/dashboard");
      return;
    }
    const g = step5LiveGoals;
    if (!g || !hasAnySetGoalEnabled(g)) {
      navigate("/revenue-analytics/dashboard");
      return;
    }
    if (!isSetGoalsStepCompleted(g)) return;
    setStep5NavBusy(true);
    setSaveError(null);
    try {
      await persistStep5GoalsAndOnboarding(g);
      navigate("/revenue-analytics/dashboard");
    } catch {
      setSaveError("Something went wrong. Try again later.");
    } finally {
      setStep5NavBusy(false);
    }
  }, [isDemoMode, step5LiveGoals, persistStep5GoalsAndOnboarding, navigate, setSaveError]);

  const updateSourceConfiguration = useCallback((sourceId: string, config: any) => {
    setSourceConfigurations(prev => ({
      ...prev,
      [sourceId]: {
        ...prev[sourceId],
        ...config
      }
    }));
  }, []);

  const handleInviteTeam = (team: TeamId) => {
    setSelectedTeam(team);
    setShowInviteModal(true);
  };

  const getStepStatus = (idx: number) => {
    return step2SubStepStatuses[idx];
  };

  const normalizeFieldName = (str: string) => str ? str.replace(/[_\s]/g, '').toLowerCase() : '';

  const handleStep2FileUpload = async (subStepIdx: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      alert("Invalid file type. Please upload a CSV file.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert(`File too large (${formatFileSize(file.size)}). Please upload a file smaller than 10MB.`);
      return;
    }

    setStep2UploadedFiles(prev => prev.map((f, i) => i === subStepIdx ? file : f));
    dispatch(setStep2FileInfo({ index: subStepIdx, fileName: file.name, fileSize: file.size }));
    setStep2SubStepStatuses(prev => prev.map((s, i) => i === subStepIdx ? 'uploaded' : s));

    try {
      const headers = await extractCSVHeaders(file);
      setStep2CsvHeaders(prev => prev.map((h, i) => i === subStepIdx ? headers : h));

      const autoMapping: Record<string, string> = {};
      const subStep = modifiedStep2Configs[subStepIdx];
      const substepFieldMapping = FieldNameMapping[subStep.key];


      if (substepFieldMapping) {
        const allFields = [...subStep.fields.required, ...subStep.fields.optional];
        allFields.forEach(field => {
          const displayFieldName = field.fieldName;
          const normRequired = normalizeFieldName(displayFieldName);
          const match = headers.find(h => normalizeFieldName(h) === normRequired);

          if (match) {
            autoMapping[displayFieldName] = match;
          }
        });
      }

      setStep2FieldMappings(prev => prev.map((m, i) => i === subStepIdx ? autoMapping : m));

    } catch (error: unknown) {
      console.error('Error extracting CSV headers:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error reading CSV headers: ${errorMessage}`);
      setStep2UploadedFiles(prev => prev.map((f, i) => i === subStepIdx ? null : f));
      setStep2SubStepStatuses(prev => prev.map((s, i) => i === subStepIdx ? 'pending' : s));
    }
  };

  const handleStep2FieldMappingChange = (subStepIdx: number, requiredField: string, csvField: string) => {
    setStep2FieldMappings(prev => prev.map((m, i) => i === subStepIdx ? { ...m, [requiredField]: csvField } : m));
  };

  const processStep2Import = async (subStepIdx: number) => {
    const file = step2UploadedFiles[subStepIdx];
    const fieldMapping = step2FieldMappings[subStepIdx];
    if (!file) return;

    setStep2IsProcessing(prev => prev.map((p, i) => i === subStepIdx ? true : p));
    setStep2SubStepStatuses(prev => prev.map((s, i) => i === subStepIdx ? 'processing' : s));

    try {
      const subStep = step2Configs[subStepIdx];
      const substepFieldMapping = FieldNameMapping[subStep.key];
      if (!substepFieldMapping) {
        throw new Error(`No field mapping found for substep: ${subStep.key}`);
      }

      const normalizeFieldName = (str: string) => str ? str.replace(/[_\s]/g, '').toLowerCase() : '';


      const isPaymentModel = selectedDataType === dataModelType.INVOICE_TRANSACTION;

      const mappingForApi: Record<string, string> = {};

      Object.entries(fieldMapping).forEach(([displayFieldName, csvHeader]) => {
        let actualFieldName;

        if (isPaymentModel && subStep.key === 'transactions') {
          actualFieldName = FieldNameMappingPaymentTransactions[displayFieldName];
        } else {
          actualFieldName = FieldNameMapping[subStep.key]?.[displayFieldName];
        }
        if (isPaymentModel && subStep.key === 'invoices') {
          actualFieldName = FieldNameMappingInvoices[displayFieldName];
        } else {
          actualFieldName = FieldNameMapping[subStep.key]?.[displayFieldName];
        }

        if (actualFieldName && csvHeader) {
          mappingForApi[actualFieldName] = csvHeader;
        }
      });

      if (subStep.key === 'transactions' && selectedDataType === dataModelType.INVOICE_TRANSACTION) {
        Object.keys(mappingForApi).forEach(k => delete mappingForApi[k]);

        Object.entries(fieldMapping).forEach(([displayFieldName, csvHeader]) => {
          const actualFieldName = FieldNameMappingPaymentTransactions[displayFieldName];
          if (actualFieldName && csvHeader) {
            mappingForApi[actualFieldName] = csvHeader;
          }
        });
      }

      const formData = new FormData();
      const operations = {
        query: `
          mutation UploadCsv($input: CsvUploadInput!) {
            uploadCsv(input: $input) {
              success
              message
              batchId
              totalRows
              processedRows
              skippedRows
              duplicateRows
              errorRows
              errors {
                rowNumber
                tempId
                error
                rowData
              }
              processingTimeMs
            }
          }
        `,
        variables: {
          input: {
            productId: getProductId() ?? "",
            environmentId: getEnvironmentId() ?? "",
            file: null,
            entityType: getEntityTypeForSubstep(subStep.key),
            dataSource: DataSource.Manual,
            fieldMappings: JSON.stringify(mappingForApi),
            skipDuplicateCheck: false,
            dataModelType: DataModelType.TransactionOnly
          }
        }
      };

      const map = { "0": ["variables.input.file"] };
      formData.append("operations", JSON.stringify(operations));
      formData.append("map", JSON.stringify(map));
      formData.append("0", file);

      const response = await fetch(`${API_ENDPOINTS.REVENUE}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${customerToken}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data || !result.data.uploadCsv) {
        throw new Error("Invalid response format from upload API");
      }

      const uploadResult = result.data.uploadCsv;
      const uiResult = {
        totalRows: uploadResult.totalRows,
        processedRows: uploadResult.processedRows,
        skippedRows: uploadResult.skippedRows,
        duplicateRows: uploadResult.duplicateRows,
        errorRows: uploadResult.errorRows,
        errors: uploadResult.errors || [],
        batchId: uploadResult.batchId,
        success: uploadResult.success,
        message: uploadResult.message,
        processingTimeMs: uploadResult.processingTimeMs,
        csvFileName: file.name,
        originalFileSize: file.size,
        savedAt: new Date().toISOString(),
        isRestored: false
      };

      setStep2ImportResults(prev => prev.map((r, i) => i === subStepIdx ? uiResult : r));

      const finalStatus = uploadResult.success && uploadResult.errorRows === 0 ? 'completed' :
        uploadResult.success && uploadResult.errorRows > 0 ? 'completed_with_errors' : 'error';
      setStep2SubStepStatuses(prev => prev.map((s, i) => i === subStepIdx ? finalStatus : s));

      handleAutoSaveStep2();

    } catch (error: any) {
      console.error('GraphQL upload failed:', error);
      setStep2SubStepStatuses(prev => prev.map((s, i) => i === subStepIdx ? 'error' : s));

      const errorResult = {
        totalRows: 0,
        processedRows: 0,
        skippedRows: 0,
        duplicateRows: 0,
        errorRows: 0,
        errors: [{ rowNumber: 0, tempId: '', error: error.message || 'Upload failed', rowData: {} }],
        batchId: '',
        success: false,
        message: error.message || 'Upload failed - check console for details',
        processingTimeMs: 0,
        csvFileName: file.name,
        originalFileSize: file.size,
        savedAt: new Date().toISOString(),
        isRestored: false
      };

      setStep2ImportResults(prev => prev.map((r, i) => i === subStepIdx ? errorResult : r));
    } finally {
      setStep2IsProcessing(prev => prev.map((p, i) => i === subStepIdx ? false : p));
    }
  };

  const retryStep2Import = (subStepIdx: number) => {
    setStep2ImportResults(prev => prev.map((r, i) => i === subStepIdx ? null : r));
    setStep2SubStepStatuses(prev => prev.map((s, i) => i === subStepIdx ? 'uploaded' : s));
    processStep2Import(subStepIdx);
  };

  const renderImportResults = (importResult: any, subStepIdx: number) => {
    if (!importResult) return null;

    const isProcessing = step2IsProcessing[subStepIdx];
    const uploadedFile = step2UploadedFiles[subStepIdx];
    const isRestored = importResult.isRestored || false;
    const resultHeading = isRestored ? 'Previous Import Results' : 'Current Import Results';


    const fileName = importResult.csvFileName || uploadedFile?.name || 'Unknown file';
    const fileSize = importResult.originalFileSize || uploadedFile?.size || 0;
    const fileDate = importResult.savedAt || importResult.uploadedAt || new Date().toISOString();
    const formattedDate = new Date(fileDate).toLocaleString();

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
      <div className="space-y-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="bg-white border rounded-lg p-4">

            {importResult.success && !isProcessing && importResult.errorRows === 0 && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">Upload completed successfully</span>
                </div>
                <p className="text-green-700 text-sm ms-[26px]">
                  {importResult.message || 'Your data has been processed and is ready for the next step'}
                </p>
              </div>
            )}

            {importResult.success && !isProcessing && importResult.errorRows > 0 && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-orange-600" />
                    <span className="text-orange-800 font-medium">Upload completed with errors</span>
                  </div>
                  <button
                    onClick={() => downloadFailedRecords(subStepIdx)}
                    className="inline-flex items-center text-sm font-medium text-orange-600 hover:text-orange-800 underline"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download Failed Records
                  </button>
                </div>

                <p className="text-orange-700 text-sm mb-4">
                  {importResult.message || `${importResult.processedRows} records processed successfully, ${importResult.errorRows} records failed`}
                </p>


                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-orange-800 text-sm">Processing Issues:</h4>
                    <ul className="space-y-1 text-sm text-orange-700">
                      {importResult.errors.slice(0, 5).map((error: any, index: number) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="text-orange-500 mt-1">•</span>
                          <span>
                            <span className="font-medium">Row {error.rowNumber}:</span> {error.error}
                          </span>
                        </li>
                      ))}
                      {importResult.errors.length > 5 && (
                        <li className="flex items-start space-x-2 text-orange-600">
                          <span className="text-orange-500 mt-1">•</span>
                          <span className="italic">
                            +{importResult.errors.length - 5} more errors
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </>
            )}


            {!importResult.success && importResult.message && !isProcessing && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="text-red-800 font-medium">Upload failed</span>
                  </div>

                  {importResult.errors && importResult.errors.some((error: any) => error.rowData && Object.keys(error.rowData).length > 0) && (
                    <button
                      onClick={() => downloadFailedRecords(subStepIdx)}
                      className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-800 underline"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download Failed Records
                    </button>
                  )}
                </div>

                <p className="text-red-700 text-sm mb-4">
                  {importResult.message}
                </p>

                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-red-800 text-sm">Error Details:</h4>
                    <ul className="space-y-1 text-sm text-red-700">
                      {importResult.errors.slice(0, 3).map((error: any, index: number) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="text-red-500 mt-1">•</span>
                          <span>{error.error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {isProcessing && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-blue-800 font-medium">Processing import...</span>
                </div>
                <p className="text-blue-700 text-sm">
                  Please wait while we process your data. This may take a few moments.
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-4 ml-2">
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">{resultHeading}</h3>
              <div className="text-sm text-gray-600 mt-1 space-y-1 mb-4 ms-2">
                <div>
                  <span className="font-medium">File:</span> {fileName}
                  {fileSize > 0 && (
                    <span className="ml-2 text-gray-500">({formatFileSize(fileSize)})</span>
                  )}
                </div>
                <div>
                  <span className="font-medium">Date:</span> {formattedDate}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1">
              {isRestored && (
                <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Restored
                </div>
              )}
              {/* {importResult.batchId && (
                <div className="text-xs text-gray-500">
                  Batch: {importResult.batchId.slice(-8)}
                </div>
              )} */}
            </div>
          </div>

          {/* Import Statistics */}
          <div className="ms-2 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900">Total Rows</h4>
              <p className="text-2xl font-bold text-gray-800">{importResult.totalRows}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900">Processed</h4>
              <p className="text-2xl font-bold text-green-600">{importResult.processedRows}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900">Skipped</h4>
              <p className="text-2xl font-bold text-yellow-600">{importResult.skippedRows}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900">Duplicates</h4>
              <p className="text-2xl font-bold text-purple-600">{importResult.duplicateRows}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900">Errors</h4>
              <p className="text-2xl font-bold text-red-600">{importResult.errorRows}</p>
            </div>
          </div>

        </div>

        {/* Data Source Link */}
        <div className="text-sm text-gray-600">
          You can check the status of all files in the {' '}
          <a
            href="/analyze/developer-tool?devTab=Revenue"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 underline"
          >
            Revenue Source Data
            <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        </div>
      </div>
    );
  };

  const isStepCompleted = (stepId: number): boolean => {

    if (isDemoMode) {
      if (demoModeSuccessSteps.includes(stepId)) {
        return true;
      }

      if (stepId === 1) {
        return selectedSources.length > 0
      }

      if (stepId === 2) {
        return checkAtLeastOneSourceCompleted();
      }

    if (stepId === 3) {
      return successSteps.includes(stepId) || isMrrCompleted;
    }

    if (stepId === 4) {
      return successSteps.includes(stepId) || selectedRevenueOptions.length > 0;
    }

    if (stepId === 5) {
      return Object.values(goals as any).some((goal: any) => !!goal?.enabled);
    }

    return false;
    }

    if (stepId === 1) {
      return getStepCompletionStatus(1)
    }

    if (stepId === 2) {
      
      return checkStep2Completion();
    }

    if (stepId === 3) {
      return successSteps.includes(stepId) || isMrrCompleted;
    }

    if (stepId === 4) {
      return successSteps.includes(stepId) || selectedRevenueOptions.length > 0;
    }

    if (stepId === 5) {
      return Object.values(goals as any).some((goal: any) => !!goal?.enabled);
    }

    if (successSteps.includes(stepId)) {
      return true;
    }

    
    
    if (stepId === 2 || stepId === 3 || stepId === 4) {
      return false; 
    }

    const module = moduleState.find((m) => m.moduleId === "revenue_analytics");
    if (!module) return false;

    const stepKey = stepsMap[stepId];
    if (!stepKey) return false;

    const subStep = module.stepDetails.find((ss) => ss.stepId === stepKey);
    const isCompleted = subStep ? subStep.isCompleted : false;
    return isCompleted;
  };

  
  const isSourceConfiguredForList = useCallback((sourceType: string, configs: Record<string, any>, healthStates: Record<string, boolean> | undefined): boolean => {
    if (sourceType === "csv") {
      const requiredSubsteps = getRequiredSubsteps(selectedDataType, step2Configs);
      return requiredSubsteps.length > 0 && requiredSubsteps.every(substepKey => {
        const substepIdx = step2Configs.findIndex(config => config.key === substepKey);
        return substepIdx !== -1 && step2ImportResults[substepIdx]?.success;
      });
    }

    const sc = configs?.[sourceType];
    if (!sc) return false;
    switch (sourceType) {
      case "api":
        return sc.status === "configured" && sc.completed === true &&
          healthStates?.["transactions-POST"] === true &&
          healthStates?.["invoices-POST"] === true;
      case "stripe":
        return (sc.status === "configured" && sc.completed === true) ||
          !!(sc.step1Completed && sc.step2Completed && sc.step3Completed && sc.step4Completed);
      case "chargebee":
        return (sc.status === "configured" && sc.completed === true) ||
          !!(sc.step1Completed && sc.step2Completed && sc.step3Completed && sc.step4Completed);
      case "quickbooks":
        return !!sc.step4Completed;
      case "salesforce":
        return sc.status === "configured" && sc.completed === true;
      default:
        return !!(sc.status === "configured" && sc.completed === true);
    }
  }, [selectedDataType, step2Configs, step2ImportResults]);

  const isSourceVisibleInList = useCallback((sourceType: string, configs: Record<string, any>, healthStates: Record<string, boolean> | undefined): boolean => {
    if (sourceType === "csv") {
      return (step2ImportResults ?? []).some((r: any) => r?.success) || isSourceConfiguredForList("csv", configs, healthStates);
    }

    const sc = configs?.[sourceType];
    if (!sc) return false;
    switch (sourceType) {
      case "api":
        return sc.status === "configured";
      case "stripe":
      case "chargebee":
      case "quickbooks":
        return (
          !!(sc.step1Completed || sc.step2Completed || sc.step3Completed || sc.step4Completed) ||
          (sc.status === "configured" && sc.completed === true)
        );
      case "salesforce":
        return sc.status === "configured";
      default:
        return sc.status === "configured";
    }
  }, [isSourceConfiguredForList, step2ImportResults]);

  const checkStep2CompletionForSource = (source: string): boolean => {
    switch (source) {
      case 'csv': {
        const requiredSubsteps = getRequiredSubsteps(selectedDataType, step2Configs);
        return requiredSubsteps.every(substepKey => {
          const substepIdx = step2Configs.findIndex(config => config.key === substepKey);
          return substepIdx !== -1 && step2ImportResults[substepIdx]?.success;
        });
      }
      case 'api':
        return sourceConfigurations.api?.status === 'configured' &&
          sourceConfigurations.api?.completed === true &&
          healthCheckCompletedStates['transactions-POST'] === true &&
          healthCheckCompletedStates['invoices-POST'] === true;
      case 'stripe':
        return (sourceConfigurations.stripe?.status === 'configured' &&
          sourceConfigurations.stripe?.completed === true) ||
          (sourceConfigurations.stripe?.step1Completed &&
            sourceConfigurations.stripe?.step2Completed &&
            sourceConfigurations.stripe?.step3Completed &&
            sourceConfigurations.stripe?.step4Completed);
      case 'chargebee':
        return (sourceConfigurations.chargebee?.status === 'configured' &&
          sourceConfigurations.chargebee?.completed === true) ||
          (sourceConfigurations.chargebee?.step1Completed &&
            sourceConfigurations.chargebee?.step2Completed &&
            sourceConfigurations.chargebee?.step3Completed &&
            sourceConfigurations.chargebee?.step4Completed);
      case 'quickbooks':
        return !!sourceConfigurations.quickbooks?.step4Completed;
      case 'salesforce':
        return sourceConfigurations.salesforce?.status === 'configured' &&
          sourceConfigurations.salesforce?.completed === true;
      default:
        return false;
    }
  };

  const checkStep2Completion = (): boolean => {
    if (selectedSources.length === 0) return false;

    
    if (activeSourceTab && selectedSources.includes(activeSourceTab)) {
      return checkStep2CompletionForSource(activeSourceTab);
    }

    
    const wasApiCompleted = isDemoMode ?
      demoModeSuccessSteps.includes(2) :
      successSteps.includes(2);

    if (wasApiCompleted) {
      return true;
    }

    
    return selectedSources.some(source => checkStep2CompletionForSource(source));
  };

  const checkAtLeastOneSourceCompleted = (): boolean => {
    if (selectedSources.length === 0) return false;

    const wasApiCompleted = isDemoMode ?
      demoModeSuccessSteps.includes(2) :
      successSteps.includes(2);

    if (wasApiCompleted) {
      return true;
    }

    return selectedSources.some(source => {
      switch (source) {
        case 'csv': {
          const requiredSubsteps = getRequiredSubsteps(selectedDataType, step2Configs);
          return requiredSubsteps.some(substepKey => {
            const substepIdx = step2Configs.findIndex(config => config.key === substepKey);
            return substepIdx !== -1 && step2ImportResults[substepIdx]?.success;
          });
        }
        case 'api':
          return sourceConfigurations.api?.status === 'configured' &&
            sourceConfigurations.api?.completed === true &&
            healthCheckCompletedStates['transactions-POST'] === true &&
            healthCheckCompletedStates['invoices-POST'] === true;
        case 'stripe':
          return (sourceConfigurations.stripe?.status === 'configured' &&
            sourceConfigurations.stripe?.completed === true) ||
            (sourceConfigurations.stripe?.step1Completed &&
              sourceConfigurations.stripe?.step2Completed &&
              sourceConfigurations.stripe?.step3Completed &&
              sourceConfigurations.stripe?.step4Completed);
        case 'chargebee':
          return (sourceConfigurations.chargebee?.status === 'configured' &&
            sourceConfigurations.chargebee?.completed === true) ||
            (sourceConfigurations.chargebee?.step1Completed &&
              sourceConfigurations.chargebee?.step2Completed &&
              sourceConfigurations.chargebee?.step3Completed &&
              sourceConfigurations.chargebee?.step4Completed);
        case 'quickbooks':
          return !!sourceConfigurations.quickbooks?.step4Completed;
        case 'salesforce':
          return sourceConfigurations.salesforce?.status === 'configured' &&
            sourceConfigurations.salesforce?.completed === true;
        default:
          return false;
      }
    });
  };

  const getCompletedSourcesCount = (): number => {
    return selectedSources.filter(source => {
      switch (source) {
        case 'csv': {
          const requiredSubsteps = getRequiredSubsteps(selectedDataType, step2Configs);
          return requiredSubsteps.some(substepKey => {
            const substepIdx = step2Configs.findIndex(config => config.key === substepKey);
            return substepIdx !== -1 && step2ImportResults[substepIdx]?.success;
          });
        }
        case 'api':
          return sourceConfigurations.api?.status === 'configured' &&
            sourceConfigurations.api?.completed === true &&
            healthCheckCompletedStates['transactions-POST'] === true &&
            healthCheckCompletedStates['invoices-POST'] === true;
        case 'stripe':
          return sourceConfigurations.stripe?.status === 'configured' &&
            sourceConfigurations.stripe?.completed === true;
        case 'chargebee':
          return sourceConfigurations.chargebee?.status === 'configured' &&
            sourceConfigurations.chargebee?.completed === true;
        case 'quickbooks':
          return !!sourceConfigurations.quickbooks?.step4Completed;
        case 'salesforce':
          return sourceConfigurations.salesforce?.status === 'configured' &&
            sourceConfigurations.salesforce?.completed === true;
        default:
          return false;
      }
    }).length;
  };

  const loadOnboardingData = () => {
    if (isDemoMode) {
      setIsReady(true);
      return;
    }

    GetCaOnboardingStepDetails({
      variables: {
        input: {
          productId: getProductId() ?? "",
          environmentId: getEnvironmentId() ?? "",
          moduleId: "revenue_analytics"
        },
      },
      onCompleted: (data: any) => {
        const modules = data?.getCAOnboardingStepDetails?.moduleDetails || [];
        applyLoadedData(modules);
        setIsReady(true);

        const state = store.getState().revenueAnalytics;
        const successStepsFromStore = state.successSteps ?? [];
        const firstIncompleteStep = findFirstIncompleteStep(successStepsFromStore);

        const configs = state.sourceConfigurations || {};
        const healthStates = state.healthCheckCompletedStates || {};
        const hasConfiguredSource = Object.keys(configs).some((src: string) =>
          isSourceVisibleInList(src, configs, healthStates)
        );

        const urlParams = new URLSearchParams(window.location.search);
        const oauthCode = urlParams.get("code");
        const oauthState = urlParams.get("state");
        let oauthSource = "stripe";
        const isOAuthReturn = !!(
          oauthCode &&
          oauthState &&
          (() => {
            try {
              const s = JSON.parse(decodeURIComponent(oauthState));
              if (s && typeof s.step === "number" && s.step === 2) {
                if (s.source) oauthSource = s.source;
                return true;
              }
              return false;
            } catch {
              return false;
            }
          })()
        );

        if (hasConfiguredSource && !isOAuthReturn) {
          setViewMode("list");
        } else if (isOAuthReturn) {
          setViewMode("setup");
          setCurrentStep(2);
          setActiveSourceTab(oauthSource);
          updateUrlWithStep(2);
        }

        if (!isOAuthReturn) {
          const urlStep = getValidStepFromUrl();
          if (urlStep && isStepAccessibleFromCompletedSteps(urlStep, successStepsFromStore)) {
            setCurrentStep(urlStep);
          } else {
            setCurrentStep(firstIncompleteStep);
            if (!urlStep) {
              updateUrlWithStep(firstIncompleteStep);
            }
          }
        }
      },
      onError: (error: any) => {
        console.error("Error loading onboarding data:", error);
        
        setIsReady(true);
        setCurrentStep(1);
      },
    });
  };


  const applyLoadedData = (modules: any[]) => {
    if (!modules || modules.length === 0) return;
    const revenueModule = modules.find((m) => m.moduleId === "revenue_analytics");
    if (!revenueModule) return;

    let currentActiveTab = '';
    revenueModule.stepDetails.forEach((step: any) => {
      try {
        const d = JSON.parse(step.value || '{}');
        if (d.activeSourceTab && (step.stepId === 'data_source' || step.stepId === 'data_setup')) {
          currentActiveTab = d.activeSourceTab;
        }
      } catch (_) {}
    });

    const merged: Partial<RevenueAnalyticsSliceState> & { sourceConfigurations: Record<string, any> } = {
      sourceConfigurations: {}
    };

    const mergeSourceConfig = (target: Record<string, any>, incoming: Record<string, any>) => {
      if (!incoming) return;
      Object.keys(incoming).forEach(sourceKey => {
        target[sourceKey] = { ...target[sourceKey], ...incoming[sourceKey] };
      });
    };

    revenueModule.stepDetails.forEach((step: any) => {
      try {
        const stepData = JSON.parse(step.value || '{}');
        switch (step.stepId) {
          case "data_source":
            if (stepData.selectedSources) merged.selectedSources = stepData.selectedSources;
            if (stepData.selectedDataType) merged.selectedDataType = stepData.selectedDataType;
            if (stepData.activeSourceTab) merged.activeSourceTab = stepData.activeSourceTab;
            if (stepData.sourceConfigurations) {
              Object.entries(stepData.sourceConfigurations).forEach(([sourceId, config]: [string, any]) => {
                if (config?.sourceCategory) {
                  merged.sourceConfigurations[sourceId] = {
                    ...merged.sourceConfigurations[sourceId],
                    sourceCategory: config.sourceCategory,
                  };
                }
              });
            }
            break;
          case "data_setup":
            if (stepData.selectedSources) merged.selectedSources = stepData.selectedSources;
            if (stepData.selectedDataType) merged.selectedDataType = stepData.selectedDataType;
            if (stepData.activeSourceTab) merged.activeSourceTab = stepData.activeSourceTab;
            if (stepData.stripeStepDetails) {
              const sd = stepData.stripeStepDetails;
              const step1 = sd.step1Completed || false;
              const step2 = sd.step2Completed || false;
              const step3 = sd.step3Completed || false;
              const step4 = sd.step4Completed || false;
              const stripeSourceConfig = stepData.sourceConfigurations?.stripe || {};
              const schemaStep = (sd.testingSteps || []).find((s: any) => s?.id === "schema");
              merged.loadedStripeData = stepData.stripeStepDetails;
              merged.sourceConfigurations.stripe = {
                ...merged.sourceConfigurations.stripe,
                ...sd.formData,
                step1Completed: step1,
                step2Completed: step2,
                step3Completed: step3,
                isStep3Incomplete: sd.isStep3Incomplete || false,
                testingSteps: sd.testingSteps || [],
                testCompleted: sd.testCompleted || false,
                step4Completed: step4,
                ingestionResult: sd.ingestionResult || null,
                schemaVerified: sd.schemaVerified || false,
                completedAt: sd.completedAt || null,
                selectedIdentifierKey: sd.selectedIdentifierKey ?? stripeSourceConfig.selectedIdentifierKey ?? schemaStep?.selectedIdentifierKey,
                customerMetaDataKeys: sd.customerMetaDataKeys ?? stripeSourceConfig.customerMetaDataKeys ?? schemaStep?.customerMetaDataKeys ?? [],
                groupIdFound: sd.groupIdFound ?? stripeSourceConfig.groupIdFound ?? false,
                status: 'configured',
                completed: step1 && step2 && step3 && step4,
                notificationEmails: sd.notificationEmails || null,
                pendingSecretKeyChange: stripeSourceConfig.pendingSecretKeyChange || false,
                pendingSyncDateChange: stripeSourceConfig.pendingSyncDateChange || false,
                step4WasEverCompleted: stripeSourceConfig.step4WasEverCompleted || sd.step4CompletedAt || false,
                step3CompletedAt: stripeSourceConfig.step3CompletedAt || null,
                step4CompletedAt: stripeSourceConfig.step4CompletedAt || null
              };
            }
            if (stepData.chargebeeStepDetails) {
              const cd = stepData.chargebeeStepDetails;
              const step1 = cd.step1Completed || false;
              const step2 = cd.step2Completed || false;
              const step3 = cd.step3Completed || false;
              const step4 = cd.step4Completed || false;
              const chargebeeSourceConfig = stepData.sourceConfigurations?.chargebee || {};
              merged.sourceConfigurations.chargebee = {
                ...merged.sourceConfigurations.chargebee,
                ...cd.formData,
                step1Completed: step1,
                step2Completed: step2,
                step3Completed: step3,
                step4Completed: step4,
                isStep3Incomplete: cd.isStep3Incomplete || false,
                testingSteps: cd.testingSteps || [],
                testCompleted: cd.testCompleted || false,
                ingestionResult: cd.ingestionResult || null,
                schemaVerified: cd.schemaVerified || false,
                completedAt: cd.completedAt || null,
                selectedIdentifierKey: cd.selectedIdentifierKey || 'group_id',
                customerMetaDataKeys: cd.customerMetaDataKeys || [],
                groupIdFound: cd.groupIdFound || false,
                status: 'configured',
                completed: step1 && step2 && step3 && step4,
                notificationEmails: cd.notificationEmails || null,
                pendingApiKeyChange: chargebeeSourceConfig.pendingApiKeyChange || false,
                pendingSyncDateChange: chargebeeSourceConfig.pendingSyncDateChange || false,
                step4WasEverCompleted: chargebeeSourceConfig.step4WasEverCompleted || cd.step4CompletedAt || false,
                step3CompletedAt: chargebeeSourceConfig.step3CompletedAt || null,
                step4CompletedAt: chargebeeSourceConfig.step4CompletedAt || null
              };
            }
            if (stepData.quickbooksStepDetails) {
              const qd = stepData.quickbooksStepDetails;
              merged.sourceConfigurations.quickbooks = {
                ...merged.sourceConfigurations.quickbooks,
                ...qd.formData,
                step1Completed: qd.step1Completed || false,
                step2Completed: qd.step2Completed || false,
                step3Completed: qd.step3Completed || false,
                step4Completed: qd.step4Completed || false,
                testCompleted: qd.testCompleted || false,
                itemsStepCompleted: qd.itemsStepCompleted || false,
                mrrItems: qd.mrrItems || [],
                ingestionResult: qd.ingestionResult || null,
                completedAt: qd.completedAt || null,
                notificationEmails: qd.notificationEmails || null,
              };
            }
            const dataSetupStep = revenueModule.stepDetails.find((s: any) => s.stepId === "data_setup");
            if (dataSetupStep && dataSetupStep.isCompleted && stepData.sourceConfigurations) {
              Object.keys(stepData.sourceConfigurations).forEach(sourceKey => {
                const sc = stepData.sourceConfigurations[sourceKey];
                if (sc && !sc.step2Completed) {
                  const isSourceCompleted = sc.completed === true || sc.status === 'configured' ||
                    (sc.step1Completed && sc.step2Completed && sc.step3Completed && sc.step4Completed);
                  if (isSourceCompleted) {
                    merged.sourceConfigurations[sourceKey] = { ...merged.sourceConfigurations[sourceKey], ...sc, step2Completed: true };
                  }
                }
              });
            }
            if (stepData.subStepsData) {
              const newImportResults = merged.step2ImportResults ?? Array.from({ length: step2Configs.length }, () => null);
              const newFieldMappings = merged.step2FieldMappings ?? Array.from({ length: step2Configs.length }, () => ({}));
              const newCsvHeaders = merged.step2CsvHeaders ?? Array.from({ length: step2Configs.length }, () => []);
              const newStatuses = merged.step2SubStepStatuses ?? Array.from({ length: step2Configs.length }, () => 'pending');
              stepData.subStepsData.forEach((subStepData: any, index: number) => {
                if (subStepData.status) newStatuses[index] = subStepData.status;
                if (subStepData.importResult) {
                  newImportResults[index] = {
                    ...subStepData.importResult,
                    isRestored: true,
                    csvFileName: subStepData.importResult.csvFileName || subStepData.fileInfo?.fileName || 'Unknown file',
                    originalFileSize: subStepData.importResult.originalFileSize || subStepData.fileInfo?.fileSize || 0
                  };
                }
                if (subStepData.csvHeaders) newCsvHeaders[index] = subStepData.csvHeaders;
                if (subStepData.fieldMapping) newFieldMappings[index] = subStepData.fieldMapping;
              });
              merged.step2ImportResults = newImportResults;
              merged.step2FieldMappings = newFieldMappings;
              merged.step2CsvHeaders = newCsvHeaders;
              merged.step2SubStepStatuses = newStatuses;
              if (stepData.subStepsData[4]?.csvStatuses) merged.csvDetectedStatuses = stepData.subStepsData[4].csvStatuses;
            }
            if (stepData.sourceConfigurations) {
              Object.keys(stepData.sourceConfigurations).forEach(sourceKey => {
                if (sourceKey !== 'stripe' && sourceKey !== 'chargebee') {
                  mergeSourceConfig(merged.sourceConfigurations, { [sourceKey]: stepData.sourceConfigurations[sourceKey] });
                } else if (sourceKey === 'chargebee' && !stepData.chargebeeStepDetails) {
                  mergeSourceConfig(merged.sourceConfigurations, { chargebee: stepData.sourceConfigurations.chargebee });
                } else if (sourceKey === 'stripe' && !stepData.stripeStepDetails) {
                  mergeSourceConfig(merged.sourceConfigurations, { stripe: stepData.sourceConfigurations.stripe });
                }
              });
            }
            Object.keys(merged.sourceConfigurations).forEach(sourceKey => {
              const sc = merged.sourceConfigurations[sourceKey];
              if (sc && !sc.step2Completed) {
                const isSourceCompleted = sc.completed === true || sc.status === 'configured' ||
                  (sc.step1Completed && sc.step2Completed && sc.step3Completed && sc.step4Completed);
                if (isSourceCompleted) merged.sourceConfigurations[sourceKey] = { ...sc, step2Completed: true };
              }
            });
            if (stepData.apiKeyInfo) {
              const apiKeyInfo = { selectedApiKeyId: stepData.apiKeyInfo.selectedApiKeyId || null, selectedApiKey: stepData.apiKeyInfo.selectedApiKey || null };
              merged.selectedApiKeyData = apiKeyInfo;
              merged.originalApiKeyData = apiKeyInfo;
              merged.apiKeyChanged = false;
              merged.apiKeyMarkedComplete = true;
              if (stepData.apiKeyInfo.selectedApiKeyId) {
                merged.sourceConfigurations.api = { ...merged.sourceConfigurations.api, status: 'configured', completed: true };
              }
            }
            if (stepData.healthCheckStates) merged.healthCheckCompletedStates = stepData.healthCheckStates;
            break;
          case "core_revenue_calculation":
            const hasFlatData = stepData.selectedMrrPlans !== undefined || stepData.selectedMrrStatuses !== undefined || stepData.baseCurrency !== undefined;
            const hasPerSourceData = !hasFlatData && stepData.sourceConfigurations && Object.keys(stepData.sourceConfigurations).some(k =>
              stepData.sourceConfigurations[k].step3Completed !== undefined || stepData.sourceConfigurations[k].selectedMrrPlans !== undefined);
            const activeTab = merged.activeSourceTab || currentActiveTab;
            if (hasFlatData) {
              if (stepData.selectedMrrPlans) merged.selectedMrrPlans = stepData.selectedMrrPlans;
              if (stepData.selectedMrrStatuses) merged.selectedMrrStatuses = stepData.selectedMrrStatuses;
              if (stepData.selectedPastDueHandling !== undefined) merged.selectedPastDueHandling = stepData.selectedPastDueHandling;
              if (stepData.includeTax !== undefined) merged.includeTax = stepData.includeTax;
              if (stepData.baseCurrency) merged.baseCurrency = stepData.baseCurrency;
              if (stepData.includeTransactionFees !== undefined) merged.includeTransactionFees = stepData.includeTransactionFees;
              if (typeof stepData.chargebeeFeePerTransaction === 'number') merged.chargebeeFeePerTransaction = stepData.chargebeeFeePerTransaction;
              if (typeof stepData.paymentGatewayFees === 'number') merged.paymentGatewayFees = stepData.paymentGatewayFees;
              if (typeof stepData.chargebeeChargesPerTxnFee === 'boolean') merged.chargebeeChargesPerTxnFee = stepData.chargebeeChargesPerTxnFee;
              else if (typeof stepData.chargebeeFeePerTransaction === 'number') merged.chargebeeChargesPerTxnFee = stepData.chargebeeFeePerTransaction > 0;
              const step3 = revenueModule.stepDetails.find((s: any) => s.stepId === "core_revenue_calculation");
              if (step3 && step3.isCompleted && stepData.sourceConfigurations) {
                Object.keys(stepData.sourceConfigurations).forEach(sourceKey => {
                  merged.sourceConfigurations[sourceKey] = { ...merged.sourceConfigurations[sourceKey], step3Completed: true };
                });
              }
            } else if (hasPerSourceData && activeTab && stepData.sourceConfigurations[activeTab]) {
              const sc = stepData.sourceConfigurations[activeTab];
              if (sc.selectedMrrPlans) merged.selectedMrrPlans = sc.selectedMrrPlans;
              if (sc.selectedMrrStatuses) merged.selectedMrrStatuses = sc.selectedMrrStatuses;
              if (sc.selectedPastDueHandling !== undefined) merged.selectedPastDueHandling = sc.selectedPastDueHandling;
              if (sc.includeTax !== undefined) merged.includeTax = sc.includeTax;
              if (sc.baseCurrency) merged.baseCurrency = sc.baseCurrency;
              if (sc.includeTransactionFees !== undefined) merged.includeTransactionFees = sc.includeTransactionFees;
              if (typeof sc.chargebeeFeePerTransaction === 'number') merged.chargebeeFeePerTransaction = sc.chargebeeFeePerTransaction;
              if (typeof sc.paymentGatewayFees === 'number') merged.paymentGatewayFees = sc.paymentGatewayFees;
              if (typeof sc.chargebeeChargesPerTxnFee === 'boolean') merged.chargebeeChargesPerTxnFee = sc.chargebeeChargesPerTxnFee;
              if (sc.step3Completed !== undefined) {
                merged.sourceConfigurations[activeTab] = { ...merged.sourceConfigurations[activeTab], ...sc, step3Completed: sc.step3Completed };
              }
            }
            break;
          case "advance_revenue_calculation":
            const hasPerSourceStep4 = stepData.sourceConfigurations && Object.keys(stepData.sourceConfigurations).some(k =>
              stepData.sourceConfigurations[k].step4Completed !== undefined || stepData.sourceConfigurations[k].selectedRevenueOptions !== undefined);
            const at = merged.activeSourceTab || currentActiveTab;
            if (hasPerSourceStep4 && at && stepData.sourceConfigurations[at]) {
              const sc = stepData.sourceConfigurations[at];
              if (sc.selectedRevenueOptions) merged.selectedRevenueOptions = sc.selectedRevenueOptions;
              if (sc.selectedChurnOptions) merged.selectedChurnOptions = sc.selectedChurnOptions;
              if (sc.step4Completed !== undefined) {
                merged.sourceConfigurations[at] = { ...merged.sourceConfigurations[at], ...sc, step4Completed: sc.step4Completed };
              }
            } else {
              const reversedData = reverseTransformStep4Data(stepData);
              merged.selectedRevenueOptions = reversedData.selectedRevenueOptions;
              merged.selectedChurnOptions = reversedData.selectedChurnOptions;
              const step4 = revenueModule.stepDetails.find((s: any) => s.stepId === "advance_revenue_calculation");
              if (step4 && step4.isCompleted && stepData.sourceConfigurations) {
                Object.keys(stepData.sourceConfigurations).forEach(sourceKey => {
                  merged.sourceConfigurations[sourceKey] = { ...merged.sourceConfigurations[sourceKey], step4Completed: true };
                });
              }
            }
            break;
          case "set_goals":
            if (stepData.goals) merged.goals = stepData.goals;
            break;
        }
      } catch (e) {
        console.error('Error parsing step data:', e);
      }
    });

    const completedSteps: number[] = [];
    revenueModule.stepDetails.forEach((step: any) => {
      if (step.isCompleted) {
        const stepNumber = Object.keys(stepsMap).find(key => stepsMap[parseInt(key)] === step.stepId);
        if (stepNumber) completedSteps.push(parseInt(stepNumber));
      }
    });
    merged.successSteps = completedSteps;

    dispatch(hydrateFromServer(merged));
  };

  const totalStepsCount = 6;

const trackDataSetupCompletion = () => {
  const state = store.getState().revenueAnalytics;
  logSetupRevenueOptimization(2, {
    activeSourceTab: state.activeSourceTab,
    sourceConfigurations: state.sourceConfigurations,
    selectedSources: state.selectedSources,
  });
};

const handleAutoSaveStep2 = () => {
  if (currentStep !== 2) return;
  const state = store.getState().revenueAnalytics;
  const currentConfigs = { ...(state.sourceConfigurations || {}) };
  let apiKeyInfo;
  if (state.originalApiKeyData && (state.originalApiKeyData.selectedApiKeyId || state.originalApiKeyData.selectedApiKey)) {
    apiKeyInfo = state.originalApiKeyData;
  } else if (state.selectedApiKeyData && (state.selectedApiKeyData.selectedApiKeyId || state.selectedApiKeyData.selectedApiKey)) {
    apiKeyInfo = state.selectedApiKeyData;
  }

  const value: any = {
    selectedSources: state.selectedSources,
    selectedDataType: state.selectedDataType,
    sourceConfigurations: currentConfigs,
    activeSourceTab: state.activeSourceTab,
    ...(apiKeyInfo && { apiKeyInfo }),
    healthCheckStates: state.healthCheckCompletedStates
  };

  if (currentConfigs.stripe && Object.keys(currentConfigs.stripe).length > 0) {
    value.stripeStepDetails = {
      step1Completed: !!(currentConfigs.stripe.environmentId && currentConfigs.stripe.stripeEnvironment),
      step2Completed: !!(currentConfigs.stripe.sourceName && currentConfigs.stripe.accountId && currentConfigs.stripe.secretKey && currentConfigs.stripe.syncStartDate),
      step3Completed: currentConfigs.stripe.step3Completed || false,
      step4Completed: currentConfigs.stripe.step4Completed || false,
      testCompleted: currentConfigs.stripe.testCompleted || false,
      isStep3Incomplete: currentConfigs.stripe.isStep3Incomplete || false,
      testingSteps: currentConfigs.stripe.testingSteps || [],
      selectedIdentifierKey: currentConfigs.stripe.selectedIdentifierKey || 'group_id',
      customerMetaDataKeys: currentConfigs.stripe.customerMetaDataKeys || [],
      groupIdFound: currentConfigs.stripe.groupIdFound || false,
      formData: {
        sourceName: currentConfigs.stripe.sourceName || 'Stripe',
        accountId: currentConfigs.stripe.accountId || '',
        secretKey: currentConfigs.stripe.secretKey || '',
        dataRetentionLocation: currentConfigs.stripe.dataRetentionLocation || '',
        syncStartDate: currentConfigs.stripe.syncStartDate || '',
        groupIdMapping: currentConfigs.stripe.groupIdMapping || false,
        environmentId: currentConfigs.stripe.environmentId || '',
        environmentName: currentConfigs.stripe.environmentName || '',
        stripeEnvironment: currentConfigs.stripe.stripeEnvironment || ''
      },
      ingestionResult: currentConfigs.stripe.ingestionResult || null,
      schemaVerified: currentConfigs.stripe.schemaVerified || false,
      completedAt: currentConfigs.stripe.completedAt || null,
      notificationEmails: currentConfigs.stripe.notificationEmails || null
    };
  } else if (state.loadedStripeData && Object.keys(state.loadedStripeData).length > 0) {
    value.stripeStepDetails = state.loadedStripeData;
  }

  if (currentConfigs.chargebee && Object.keys(currentConfigs.chargebee).length > 0) {
    value.chargebeeStepDetails = {
      step1Completed: !!(currentConfigs.chargebee.environmentId && currentConfigs.chargebee.chargebeeEnvironment),
      step2Completed: !!(currentConfigs.chargebee.sourceName && currentConfigs.chargebee.subdomain && currentConfigs.chargebee.apiKey && currentConfigs.chargebee.syncStartDate),
      step3Completed: currentConfigs.chargebee.step3Completed || false,
      step4Completed: currentConfigs.chargebee.step4Completed || false,
      testCompleted: currentConfigs.chargebee.testCompleted || false,
      isStep3Incomplete: currentConfigs.chargebee.isStep3Incomplete || false,
      testingSteps: currentConfigs.chargebee.testingSteps || [],
      selectedIdentifierKey: currentConfigs.chargebee.selectedIdentifierKey || 'group_id',
      customerMetaDataKeys: currentConfigs.chargebee.customerMetaDataKeys || [],
      groupIdFound: currentConfigs.chargebee.groupIdFound || false,
      formData: {
        sourceName: currentConfigs.chargebee.sourceName || 'Chargebee',
        subdomain: currentConfigs.chargebee.subdomain || '',
        apiKey: currentConfigs.chargebee.apiKey || '',
        secretKey: currentConfigs.chargebee.secretKey || '',
        productCatalog: currentConfigs.chargebee.productCatalog || '2.0',
        dataRetentionLocation: currentConfigs.chargebee.dataRetentionLocation || '',
        syncStartDate: currentConfigs.chargebee.syncStartDate || '',
        groupIdMapping: currentConfigs.chargebee.groupIdMapping || false,
        environmentId: currentConfigs.chargebee.environmentId || '',
        environmentName: currentConfigs.chargebee.environmentName || '',
        chargebeeEnvironment: currentConfigs.chargebee.chargebeeEnvironment || ''
      },
      ingestionResult: currentConfigs.chargebee.ingestionResult || null,
      schemaVerified: currentConfigs.chargebee.schemaVerified || false,
      completedAt: currentConfigs.chargebee.completedAt || null,
      notificationEmails: currentConfigs.chargebee.notificationEmails || null
    };
  }

  if (currentConfigs.quickbooks && Object.keys(currentConfigs.quickbooks).length > 0) {
    value.quickbooksStepDetails = {
      step1Completed: currentConfigs.quickbooks.step1Completed || false,
      step2Completed: currentConfigs.quickbooks.step2Completed || false,
      step3Completed: currentConfigs.quickbooks.step3Completed || false,
      step4Completed: currentConfigs.quickbooks.step4Completed || false,
      testCompleted: currentConfigs.quickbooks.testCompleted || false,
      itemsStepCompleted: currentConfigs.quickbooks.itemsStepCompleted || false,
      mrrItems: currentConfigs.quickbooks.mrrItems || [],
      formData: {
        sourceName: currentConfigs.quickbooks.sourceName || 'QuickBooks',
        startDate: currentConfigs.quickbooks.startDate || '',
        realmId: currentConfigs.quickbooks.realmId || '',
        companyName: currentConfigs.quickbooks.companyName || '',
        environmentId: currentConfigs.quickbooks.environmentId || '',
        quickbooksEnvironment: currentConfigs.quickbooks.quickbooksEnvironment || '',
        sandbox: currentConfigs.quickbooks.sandbox || false,
      },
      ingestionResult: currentConfigs.quickbooks.ingestionResult || null,
      completedAt: currentConfigs.quickbooks.completedAt || null,
      notificationEmails: currentConfigs.quickbooks.notificationEmails || null,
    };
  }

  value.subStepsData = step2Configs.map((config, index) => {
    const importResult = state.step2ImportResults[index];
    const uploadedFile = step2UploadedFiles[index];
    const fileInfoFromStore = state.step2FileInfos[index];
    const fieldMapping = state.step2FieldMappings[index];
    const csvHeaders = state.step2CsvHeaders[index];
    const csvStatuses = index === 6 ? state.csvDetectedStatuses : [];

    const fileInfo = uploadedFile
      ? { fileName: uploadedFile.name, fileSize: uploadedFile.size, uploadedAt: new Date().toISOString() }
      : fileInfoFromStore?.fileName ? { fileName: fileInfoFromStore.fileName, fileSize: fileInfoFromStore.fileSize, uploadedAt: new Date().toISOString() } : null;

    return {
      key: config.key,
      title: config.title,
      status: state.step2SubStepStatuses[index],
      fileInfo,
      csvHeaders: csvHeaders || [],
      csvStatuses: csvStatuses || [],
      fieldMapping: fieldMapping || {},
      importResult: importResult ? {
        totalRows: importResult.totalRows || 0,
        processedRows: importResult.processedRows || 0,
        skippedRows: importResult.skippedRows || 0,
        duplicateRows: importResult.duplicateRows || 0,
        errorRows: importResult.errorRows || 0,
        hasErrors: !!(importResult.errors && importResult.errors.length > 0),
        errorCount: importResult.errors ? importResult.errors.length : 0,
        firstFewErrors: importResult.errors ? importResult.errors.slice(0, 3) : [],
        batchId: importResult.batchId || '',
        success: importResult.success || false,
        message: importResult.message || '',
        processingTimeMs: importResult.processingTimeMs || 0,
        savedAt: new Date().toISOString(),
        csvFileName: uploadedFile?.name || importResult.csvFileName || 'Unknown file',
        originalFileSize: uploadedFile?.size || importResult.originalFileSize || 0
      } : null
    };
  });

  Object.keys(currentConfigs).forEach(sourceKey => {
    const sourceConfig = currentConfigs[sourceKey];
    if (sourceConfig) {
      const isSourceCompleted = checkStep2CompletionForSource(sourceKey);
      currentConfigs[sourceKey] = { ...sourceConfig, step2Completed: isSourceCompleted };
    }
  });

  value.completionSummary = {
    totalSubSteps: state.selectedSources.length,
    completedSubSteps: state.selectedSources.filter(source => {
      switch (source) {
        case 'csv': {
          const requiredSubsteps = getRequiredSubsteps(state.selectedDataType, step2Configs);
          return requiredSubsteps.some(substepKey => {
            const substepIdx = step2Configs.findIndex(config => config.key === substepKey);
            return substepIdx !== -1 && state.step2ImportResults[substepIdx] !== null;
          });
        }
        case 'api':
          return currentConfigs.api?.status === 'configured' && currentConfigs.api?.completed === true &&
            state.healthCheckCompletedStates['transactions-POST'] === true && state.healthCheckCompletedStates['invoices-POST'] === true;
        case 'stripe':
          return currentConfigs.stripe?.status === 'configured' && currentConfigs.stripe?.completed === true;
        case 'salesforce':
          return currentConfigs.salesforce?.status === 'configured' && currentConfigs.salesforce?.completed === true;
        default:
          return false;
      }
    }).length,
    isFullyCompleted: checkStep2Completion(),
    autoSavedAt: new Date().toISOString(),
    hasAnyImportAttempts: state.step2ImportResults.some(result => result !== null)
  };

  saveStep(value, true);
  dispatch(setRevenueAnalyticsField({ key: 'sourceConfigurations', value: currentConfigs }));

  const wasApiCompleted = isDemoMode ? state.demoModeSuccessSteps.includes(2) : state.successSteps.includes(2);
  if (checkStep2Completion() && !wasApiCompleted) {
    const currentlyComplete = state.selectedSources.every(source => {
      switch (source) {
        case 'csv': {
          const requiredSubsteps = getRequiredSubsteps(state.selectedDataType, step2Configs);
          return requiredSubsteps.every(substepKey => {
            const substepIdx = step2Configs.findIndex(config => config.key === substepKey);
            return substepIdx !== -1 && state.step2ImportResults[substepIdx]?.success;
          });
        }
        case 'api':
          return currentConfigs.api?.status === 'configured' && currentConfigs.api?.completed === true &&
            state.healthCheckCompletedStates['transactions-POST'] === true && state.healthCheckCompletedStates['invoices-POST'] === true;
        case 'stripe':
          return currentConfigs.stripe?.status === 'configured' && currentConfigs.stripe?.completed === true;
        case 'salesforce':
          return currentConfigs.salesforce?.status === 'configured' && currentConfigs.salesforce?.completed === true;
        default:
          return false;
      }
    });
    if (currentlyComplete) {
      if (isDemoMode) {
        dispatch(setRevenueAnalyticsField({ key: 'demoModeSuccessSteps', value: [...state.demoModeSuccessSteps, 2] }));
      } else {
        dispatch(setRevenueAnalyticsField({ key: 'successSteps', value: [...state.successSteps, 2] }));
      }
    }
  }
};

  function findFirstIncompleteStep(completedSteps: number[]): number {
    for (let step = 1; step <= totalStepsCount; step++) {
      if (!completedSteps.includes(step)) {
        return step;
      }
    }
    return totalStepsCount;
  }

  const saveStep = (value: any, skipTracking = false) => {
    if (isDemoMode) {
      setProcessing(false);
      return;
    }

    try {
      const stepCompletionStatus = getStepCompletionStatus(currentStep);
      storeCustomerAnalyticsOnboarding({
        variables: {
          input: {
            environmentId: getEnvironmentId() ?? "",
            productId: getProductId() ?? "",
            isCompleted: stepCompletionStatus === true,
            moduleId: "revenue_analytics",
            stepId: stepsMap[currentStep as keyof typeof stepsMap],
            data: JSON.stringify(value)
          }
        },
        onCompleted: (data) => {
          const stepKey = stepsMap[currentStep as keyof typeof stepsMap];
          if (!stepKey) return;

          if (!skipTracking && currentStep !== 2) logSetupRevenueOptimization(currentStep, value);
          
          if (currentStep !== 2 && currentStep !== 3 && currentStep !== 4) {
            dispatch(
              updateStepStatus({
                path: `revenue_analytics.${stepKey}`,
                value: JSON.stringify(value),
                isCompleted: getStepCompletionStatus(currentStep)
              })
            );
          } else {
            dispatch(
              updateStepStatus({
                path: `revenue_analytics.${stepKey}`,
                value: JSON.stringify(value),
                isCompleted: false
              })
            );
          }
        },
        onError: (error) => {
          setSaveError("Something went wrong. Try again later.");
        }
      });
    } catch (error) {
      setSaveError("Something went wrong. Try again later.");
    } finally {
      setProcessing(false);
    }
  };

  const getStepCompletionStatus = (stepId: number) => {
    switch (stepId) {
      case 1:
        return selectedSources.length > 0 && (!['csv', 'api'].some(s => selectedSources.includes(s)) || selectedDataType.length > 0);
      case 2:
        return checkStep2Completion();
      case 3:
        return successSteps.includes(3) || isMrrCompleted;
      case 4:
        return successSteps.includes(4) || selectedRevenueOptions.length > 0;
      case 5:
        return isSetGoalsStepCompleted(goals as Goals);
      case 6:
        return false; 
      case 7:
        return true; 
      default:
        return true;
    }
  };

  function logSetupRevenueOptimization(currentStep: number, value: any) {
    if (isDemoMode) return;

    const eventName = stepsEventNameMap[currentStep];
    const step_id = stepsMap[currentStep];
    const userId = getUser()?.id;

    const eventNames = eventName ? [{ event_name: eventName }] : [];
    if (currentStep === 1 && value && typeof value === "object") {
      const selectedSources = Array.isArray(value.selectedSources) ? value.selectedSources : [];
      const sourceConfigurations =
        value.sourceConfigurations && typeof value.sourceConfigurations === "object"
          ? value.sourceConfigurations
          : {};
      const categoryToEventPrefix: Record<string, string> = {
        subscription: "subscription",
        transactions: "transaction",
      };
      const normalizedSelectedSources = selectedSources
        .map((source: unknown) => String(source ?? "").toLowerCase().trim())
        .filter((source: string) => source.length > 0);
      const activeSourceTab = String(value.activeSourceTab ?? "").toLowerCase().trim();
      const selectedSource = normalizedSelectedSources.includes(activeSourceTab)
        ? activeSourceTab
        : normalizedSelectedSources[0];
      if (selectedSource) {
        const rawCategory = (sourceConfigurations[selectedSource]?.sourceCategory as string | undefined) ?? "";
        const category = rawCategory.toLowerCase().trim();
        const eventPrefix = categoryToEventPrefix[category];
        if (eventPrefix) {
          eventNames.push({ event_name: `ro_data_source_${eventPrefix}_${selectedSource}` });
        }
      }
    }

    if (currentStep === 2 && value && typeof value === "object") {
      const sourceConfigurations =
        value.sourceConfigurations && typeof value.sourceConfigurations === "object"
          ? value.sourceConfigurations
          : {};
      const activeSourceTab = String(value.activeSourceTab ?? "").toLowerCase().trim();
      const sourceConfig = activeSourceTab ? (sourceConfigurations[activeSourceTab] || {}) : {};
      // Only fire step-2 events when the integration's last step (ingestion) is fully done
      if (!sourceConfig.completed) return;
      if (activeSourceTab) {
        const rawCategory = (sourceConfig.sourceCategory as string | undefined) ?? "";
        const category = rawCategory.toLowerCase().trim();
        if (category) {
          eventNames.push({ event_name: `ro_data_setup_${category}_${activeSourceTab}` });
        }
      }
    }

    const trackPayload = eventNames.map(({ event_name }) => ({
      event_name,
      properties: {
        step_id,
        module_id: "revenue_analytics",
        completion_status: "completed",
        data: JSON.stringify(value)
      },
      user_id: userId,
      timestamp: new Date().toISOString(),
      context: {
        group_id: getAccountId() ?? "unknown"
      }
    }));

    const signature = `${step_id}|${JSON.stringify({
      eventNames: eventNames.map(e => e.event_name),
      properties: {
        step_id,
        module_id: "revenue_analytics",
        completion_status: "completed",
        data: JSON.stringify(value)
      },
      user_id: userId,
      context: {
        group_id: getAccountId() ?? "unknown"
      }
    })}`;
    const now = Date.now();
    if (
      lastTrackedPayloadRef.current &&
      lastTrackedPayloadRef.current.signature === signature &&
      now - lastTrackedPayloadRef.current.timestamp < 3000
    ) {
      return;
    }
    lastTrackedPayloadRef.current = { signature, timestamp: now };

    if (window.thriveStack && typeof (window.thriveStack as any).track === "function") {
      try {
        (window.thriveStack as any).track(trackPayload);
      } catch (error) {
        console.error("Analytics tracking failed:", error);
      }
    }
  }

  const getStatusDisplay = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      'pending': { color: 'bg-gray-100 text-gray-600', label: 'Pending' },
      'uploaded': { color: 'bg-yellow-50 text-yellow-800', label: 'Uploaded' },
      'processing': { color: 'bg-blue-50 text-blue-800', label: 'Processing...' },
      'completed': { color: 'bg-green-50 text-green-800', label: 'Completed' },
      'completed_with_errors': { color: 'bg-orange-10 text-orange-800', label: 'Completed (Errors)' },
      'error': { color: 'bg-red-50 text-red-800', label: 'Error' }
    };

    return statusConfig[status] || statusConfig['pending'];
  };

  const getImportButtonText = (status: string, isProcessing: boolean) => {
    if (isProcessing) return 'Processing...';
    if (status === 'error') return 'Retry Import';
    if (status === 'completed' || status === 'completed_with_errors') return 'Re-import';
    return 'Proceed to Import';
  };

  const handleDownloadAllSamples = () => {
    const zip = new JSZip();
    const sampleData = generateSampleDataForDownload(42);

    const updateDatesInSampleData = (data: string[][], fieldConfig: any) => {
      const rng = new SeededRandom(Date.now());
      const allFields = [...fieldConfig.fields.required, ...fieldConfig.fields.optional];

      const generateRandomDate = (daysBack: number = 90): Date => {
        const now = new Date();
        return new Date(now.getTime() - (rng.next() * daysBack * 24 * 60 * 60 * 1000));
      };

      const formatDate = (date: Date, includeTime: boolean = false): string => {
        if (includeTime) {
          return date.toISOString();
        } else {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      };

      return data.map(row => {
        let periodStartDate: Date | null = null;
        let transactionDate: Date | null = null;
        let periodEndDate: Date | null = null;
        let issueDate: Date | null = null;
        let dueDate: Date | null = null;

        const hasPeriodStart = allFields.some(f => f.fieldName === TRANSACTION_FIELDS.PERIOD_START_DATE);
        const hasTransactionDate = allFields.some(f => f.fieldName === TRANSACTION_FIELDS.DATE);
        const hasPeriodEnd = allFields.some(f => f.fieldName === TRANSACTION_FIELDS.PERIOD_END_DATE);

        if (hasPeriodStart || hasTransactionDate || hasPeriodEnd) {
          const baseDate = generateRandomDate(90);
          periodStartDate = new Date(baseDate);
          periodStartDate.setDate(periodStartDate.getDate() - rng.nextInt(15, 30));

          transactionDate = new Date(periodStartDate);
          transactionDate.setDate(transactionDate.getDate() + rng.nextInt(5, 10));

          periodEndDate = new Date(transactionDate);
          periodEndDate.setDate(periodEndDate.getDate() + rng.nextInt(15, 25));
        }

        const hasIssueDate = allFields.some(f => f.fieldName === INVOICE_FIELDS.INVOICE_ISSUE_DATE);
        const hasDueDate = allFields.some(f => f.fieldName === INVOICE_FIELDS.INVOICE_DUE_DATE);

        if (hasIssueDate || hasDueDate) {
          issueDate = generateRandomDate(90);
          dueDate = new Date(issueDate);
          dueDate.setDate(dueDate.getDate() + rng.nextInt(15, 45));
        }

        return row.map((value, index) => {
          if (index >= allFields.length) return value;

          const fieldName = allFields[index].fieldName;

          if (fieldName === TRANSACTION_FIELDS.PERIOD_START_DATE && periodStartDate) {
            return formatDate(periodStartDate);
          } else if (fieldName === TRANSACTION_FIELDS.DATE && transactionDate) {
            return formatDate(transactionDate, true);
          } else if (fieldName === TRANSACTION_FIELDS.PERIOD_END_DATE && periodEndDate) {
            return formatDate(periodEndDate);
          } else if (fieldName === INVOICE_FIELDS.INVOICE_ISSUE_DATE && issueDate) {
            return formatDate(issueDate);
          } else if (fieldName === INVOICE_FIELDS.INVOICE_DUE_DATE && dueDate) {
            return formatDate(dueDate);
          } else if (value && typeof value === 'string' && value.includes('T') && value.includes('Z')) {
            const randomDate = generateRandomDate(90);
            return formatDate(randomDate, true);
          }

          return value;
        });
      });
    };

    if (selectedDataType === dataModelType.INVOICE_TRANSACTION) {
      const fieldsToProcess = [paymentTransactionsFields, invoicesFields];

      fieldsToProcess.forEach((config) => {
        const data = sampleData[config.key];
        if (data) {
          const updatedData = updateDatesInSampleData(data, config);
          const allFields = [...config.fields.required, ...config.fields.optional];
          const headers = allFields.map(field => field.fieldName);
          let csvContent = headers.join(',') + '\n';
          csvContent += updatedData.map(row => row.join(',')).join('\n') + '\n';
          zip.file(config.sampleFileName, csvContent);
        }
      });
    } else {
      step2Configs.forEach((config, index) => {
        const data = sampleData[config.key];
        if (data) {
          const updatedData = updateDatesInSampleData(data, config);
          const allFields = [...config.fields.required, ...config.fields.optional];
          const headers = allFields.map(field => field.fieldName);
          let csvContent = headers.join(',') + '\n';
          csvContent += updatedData.map(row => row.join(',')).join('\n') + '\n';
          zip.file(config.sampleFileName, csvContent);
        }
      });
    }

    zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'revenue_intelligence_sample_files.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  const downloadFailedRecords = (subStepIdx: number) => {
    const importResult = step2ImportResults[subStepIdx];
    if (!importResult || !importResult.errors || importResult.errors.length === 0) {
      return;
    }

    const errorData = importResult.errors.map((error: any) => ({
      row: error.rowNumber,
      tempId: error.tempId || '',
      error: error.error,
      data: JSON.stringify(error.rowData || {})
    }));

    const csvContent = 'Row,Temp ID,Error,Data\n' +
      errorData.map((item: any) => `${item.row},"${item.tempId}","${item.error}","${item.data}"`).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${step2Configs[subStepIdx].key}_failed_records.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const modifiedStep2Configs = step2Configs.map(config => {
    if (selectedDataType === dataModelType.INVOICE_TRANSACTION && config.key === 'transactions') {
      return {
        ...config,
        fields: {
          required: paymentTransactionsFields.fields.required,
          optional: paymentTransactionsFields.fields.optional
        }
      };
    }
    if (selectedDataType === dataModelType.INVOICE_TRANSACTION && config.key === 'invoices') {
      return {
        ...config,
        fields: {
          required: invoicesFields.fields.required,
          optional: invoicesFields.fields.optional
        }
      };
    }
    return config;
  });


  function SkipAndTryDemo() {
    cleanUrlStep();
    toggleDemoMode('/analyze/revenue-analytics/dashboard');
  }

  const isCurrentStepCompleted = () => {
    if (currentStep === 2) {
      return checkAtLeastOneSourceCompleted();
    }
    return getStepCompletionStatus(currentStep);
  };

  const onStepComplete = (next: boolean = true, overrideValues?: any, saveAnyway: boolean = false) => {
    if (!overrideValues && !isCurrentStepCompleted() && !saveAnyway) {
      return;
    }

    if (overrideValues && currentStep === 2 && apiKeyChanged) {
      setApiKeyMarkedComplete(true);
    }

    setSaveError(null);

    let value: any;
    if (currentStep === 1) {
      const sourceCategoryConfig = Object.fromEntries(
        selectedSources
          .filter(id => sourceConfigurations[id]?.sourceCategory)
          .map(id => [id, { sourceCategory: sourceConfigurations[id].sourceCategory }])
      );
      value = {
        selectedSources,
        selectedDataType,
        activeSourceTab,
        ...(Object.keys(sourceCategoryConfig).length > 0 && { sourceConfigurations: sourceCategoryConfig }),
      };
    } else if (currentStep === 2) {
      let apiKeyInfo;
      if (overrideValues) {
        apiKeyInfo = overrideValues;
      } else if (originalApiKeyData && (originalApiKeyData.selectedApiKeyId || originalApiKeyData.selectedApiKey)) {
        apiKeyInfo = originalApiKeyData;
      } else if (selectedApiKeyData && (selectedApiKeyData.selectedApiKeyId || selectedApiKeyData.selectedApiKey)) {
        apiKeyInfo = selectedApiKeyData;
      }

      const configs = sourceConfigurations || {};
      value = {
        selectedSources,
        selectedDataType,
        sourceConfigurations: configs,
        activeSourceTab,
        ...(apiKeyInfo && { apiKeyInfo }),
        healthCheckStates: overrideValues?.healthCheckStates || healthCheckCompletedStates,
      };

      if (configs.stripe && Object.keys(configs.stripe).length > 0) {
        const stripe = configs.stripe;
        value.stripeStepDetails = {
          step1Completed: !!(stripe.environmentId && stripe.stripeEnvironment),
          step2Completed: !!(stripe.sourceName && stripe.accountId && stripe.secretKey && stripe.syncStartDate),
          step3Completed: stripe.step3Completed || false,
          testCompleted: stripe.testCompleted || false,
          step4Completed: stripe.step4Completed || false,
          isStep3Incomplete: stripe.isStep3Incomplete || false,
          testingSteps: stripe.testingSteps || [],
          selectedIdentifierKey: stripe.selectedIdentifierKey || 'group_id',
          customerMetaDataKeys: stripe.customerMetaDataKeys || [],
          groupIdFound: stripe.groupIdFound || false, 
          formData: {
            sourceName: stripe.sourceName || 'Stripe',
            accountId: stripe.accountId || '',
            secretKey: stripe.secretKey || '',
            dataRetentionLocation: stripe.dataRetentionLocation || '',
            syncStartDate: stripe.syncStartDate || '',
            groupIdMapping: stripe.groupIdMapping || false,
            environmentId: stripe.environmentId || '',
            environmentName: stripe.environmentName || '',
            stripeEnvironment: stripe.stripeEnvironment || ''
          },
          ingestionResult: stripe.ingestionResult || null,
          schemaVerified: stripe.schemaVerified || false,
          completedAt: stripe.completedAt || null,
          notificationEmails: stripe.notificationEmails || null
        };
      }

      if (configs.chargebee && Object.keys(configs.chargebee).length > 0) {
        const chargebee = configs.chargebee;
        value.chargebeeStepDetails = {
          step1Completed: !!(chargebee.environmentId && chargebee.chargebeeEnvironment),
          step2Completed: !!(chargebee.sourceName && chargebee.subdomain && chargebee.apiKey && chargebee.syncStartDate),
          step3Completed: chargebee.step3Completed || false,
          testCompleted: chargebee.testCompleted || false,
          step4Completed: chargebee.step4Completed || false,
          isStep3Incomplete: chargebee.isStep3Incomplete || false,
          testingSteps: chargebee.testingSteps || [],
          selectedIdentifierKey: chargebee.selectedIdentifierKey || 'group_id',
          customerMetaDataKeys: chargebee.customerMetaDataKeys || [],
          groupIdFound: chargebee.groupIdFound || false,
          formData: {
            sourceName: chargebee.sourceName || 'Chargebee',
            subdomain: chargebee.subdomain || '',
            apiKey: chargebee.apiKey || '',
            secretKey: chargebee.secretKey || '',
            productCatalog: chargebee.productCatalog || '2.0',
            dataRetentionLocation: chargebee.dataRetentionLocation || '',
            syncStartDate: chargebee.syncStartDate || '',
            groupIdMapping: chargebee.groupIdMapping || false,
            environmentId: chargebee.environmentId || '',
            environmentName: chargebee.environmentName || '',
            chargebeeEnvironment: chargebee.chargebeeEnvironment || ''
          },
          ingestionResult: chargebee.ingestionResult || null,
          schemaVerified: chargebee.schemaVerified || false,
          completedAt: chargebee.completedAt || null,
          notificationEmails: chargebee.notificationEmails || null
        };
      }

      if (configs.quickbooks && Object.keys(configs.quickbooks).length > 0) {
        const qb = configs.quickbooks;
        value.quickbooksStepDetails = {
          step1Completed: qb.step1Completed || false,
          step2Completed: qb.step2Completed || false,
          step3Completed: qb.step3Completed || false,
          step4Completed: qb.step4Completed || false,
          testCompleted: qb.testCompleted || false,
          itemsStepCompleted: qb.itemsStepCompleted || false,
          mrrItems: qb.mrrItems || [],
          formData: {
            sourceName: qb.sourceName || 'QuickBooks',
            startDate: qb.startDate || '',
            realmId: qb.realmId || '',
            companyName: qb.companyName || '',
            environmentId: qb.environmentId || '',
            quickbooksEnvironment: qb.quickbooksEnvironment || '',
            sandbox: qb.sandbox || false,
          },
          ingestionResult: qb.ingestionResult || null,
          completedAt: qb.completedAt || null,
          notificationEmails: qb.notificationEmails || null,
        };
      }

      value.subStepsData = step2Configs.map((config, index) => {
        const importResult = step2ImportResults[index];
        const uploadedFile = step2UploadedFiles[index];
        const fieldMapping = step2FieldMappings[index];
        const csvHeaders = step2CsvHeaders[index];
        const csvStatuses = index === 6 ? csvDetectedStatuses : [];

        return {
          key: config.key,
          title: config.title,
          status: step2SubStepStatuses[index],
          fileInfo: uploadedFile ? {
            fileName: uploadedFile.name,
            fileSize: uploadedFile.size,
            uploadedAt: new Date().toISOString()
          } : null,
          csvHeaders: csvHeaders || [],
          csvStatuses: csvStatuses || [],
          fieldMapping: fieldMapping || {},
          importResult: importResult ? {
            totalRows: importResult.totalRows || 0,
            processedRows: importResult.processedRows || 0,
            skippedRows: importResult.skippedRows || 0,
            duplicateRows: importResult.duplicateRows || 0,
            errorRows: importResult.errorRows || 0,
            hasErrors: !!(importResult.errors && importResult.errors.length > 0),
            errorCount: importResult.errors ? importResult.errors.length : 0,
            firstFewErrors: importResult.errors ? importResult.errors.slice(0, 3) : [],
            batchId: importResult.batchId || '',
            success: importResult.success || false,
            message: importResult.message || '',
            processingTimeMs: importResult.processingTimeMs || 0,
            savedAt: new Date().toISOString(),
            csvFileName: uploadedFile?.name || importResult.csvFileName || 'Unknown file',
            originalFileSize: uploadedFile?.size || importResult.originalFileSize || 0
          } : null
        };
      });

      value.completionSummary = {
        totalSubSteps: selectedSources.length,
        completedSubSteps: selectedSources.filter(source => {
          switch (source) {
            case 'csv': {
              const requiredSubsteps = getRequiredSubsteps(selectedDataType, step2Configs);
              return requiredSubsteps.some(substepKey => {
                const substepIdx = step2Configs.findIndex(config => config.key === substepKey);
                return substepIdx !== -1 && step2ImportResults[substepIdx] !== null;
              });
            }
            case 'api':
              return sourceConfigurations.api?.status === 'configured' && sourceConfigurations.api?.completed === true;
            case 'stripe':
              return sourceConfigurations.stripe?.status === 'configured' && sourceConfigurations.stripe?.completed === true;
            case 'salesforce':
              return sourceConfigurations.salesforce?.status === 'configured' && sourceConfigurations.salesforce?.completed === true;
            default:
              return false;
          }
        }).length,
        isFullyCompleted: checkStep2Completion(),
        autoSavedAt: new Date().toISOString(),
        hasAnyImportAttempts: step2ImportResults.some(result => result !== null)
      };

    }

    else if (currentStep === 3) {
      
      const updatedSourceConfigurations = { ...sourceConfigurations };
      
      if (overrideValues) {
        
        if (activeSourceTab && updatedSourceConfigurations[activeSourceTab]) {
          // Remove old nested fields and only keep essential source configuration
          const { 
            selectedMrrPlans: _oldPlans, 
            selectedMrrStatuses: _oldStatuses, 
            selectedPastDueHandling: _oldPastDue, 
            includeTax: _oldIncludeTax, 
            baseCurrency: _oldBaseCurrency, 
            includeTransactionFees: _oldIncludeTxnFees,
            chargebeeFeePerTransaction: _oldChargebeeFee,
            paymentGatewayFees: _oldGatewayFees,
            chargebeeChargesPerTxnFee: _oldChargebeeCharges,
            ...cleanedSourceConfig 
          } = updatedSourceConfigurations[activeSourceTab];
          
          updatedSourceConfigurations[activeSourceTab] = {
            ...cleanedSourceConfig,
            step3Completed: true,
            step3CompletedAt: new Date().toISOString()
          };
        }
        
        value = {
          selectedMrrPlans: overrideValues.selectedMrrPlans,
          selectedMrrStatuses: overrideValues.selectedMrrStatuses,
          selectedPastDueHandling: overrideValues.selectedPastDueHandling,
          includeTax: overrideValues.includeTax,
          baseCurrency: overrideValues.baseCurrency,
          includeTransactionFees: overrideValues.includeTransactionFees,
          chargebeeFeePerTransaction: overrideValues.chargebeeFeePerTransaction || chargebeeFeePerTransaction,
          paymentGatewayFees: overrideValues.paymentGatewayFees || paymentGatewayFees,
          chargebeeChargesPerTxnFee: overrideValues.chargebeeChargesPerTxnFee || chargebeeChargesPerTxnFee,
          user_email: getUser()?.email,
          user_name: getUser()?.name
        };
      } else {
        value = {
          selectedMrrPlans,
          selectedMrrStatuses,
          selectedPastDueHandling,
          includeTax,
          baseCurrency,
          includeTransactionFees,
          chargebeeFeePerTransaction,
          paymentGatewayFees,
          chargebeeChargesPerTxnFee,
          user_email: getUser()?.email,
          user_name: getUser()?.name
        };
      }
      if (activeSourceTab && updatedSourceConfigurations[activeSourceTab]) {
        updatedSourceConfigurations[activeSourceTab] = {
          ...updatedSourceConfigurations[activeSourceTab],
          step3Completed: true,
          step3CompletedAt: new Date().toISOString()
        };
      }
      setSourceConfigurations(updatedSourceConfigurations);
    } else if (currentStep === 4) {
      
      const updatedSourceConfigurations = { ...sourceConfigurations };
      
      if (activeSourceTab && updatedSourceConfigurations[activeSourceTab]) {
        const step4Data = transformStep4Data(selectedRevenueOptions, selectedChurnOptions);
        updatedSourceConfigurations[activeSourceTab] = {
          ...updatedSourceConfigurations[activeSourceTab],
          ...step4Data,
          selectedRevenueOptions,
          selectedChurnOptions,
          step4Completed: true,
          step4CompletedAt: new Date().toISOString()
        };
        
        value = {
          sourceConfigurations: updatedSourceConfigurations
        };
      } else {
        value = transformStep4Data(selectedRevenueOptions, selectedChurnOptions);
      }
      
      setSourceConfigurations(updatedSourceConfigurations);
    } else if (currentStep === 5) {
      value = {
        goals
      };
    } else if (currentStep === 6) {
      value = {
        completed: true
      };
    } else if (currentStep === 7) {
      value = {
        completed: true
      };
    }

    if (currentStep === steps.length) {
      setProcessing(true);
      cleanUrlStep();
      navigate('/revenue-analytics/dashboard');
      return;
    }

    if (isDemoMode) {
      if (!demoModeSuccessSteps.includes(currentStep)) {
        setDemoModeSuccessSteps(prevSteps => [...prevSteps, currentStep]);
      }
    } else {
      if (!successSteps.includes(currentStep)) {
        setSuccessSteps(prevSteps => [...prevSteps, currentStep]);
      }
    }

    saveStep(value);

    if (next) {
      const nextStep = Math.min(currentStep + 1, steps.length);
      // Demo-only: when completing step 4, add current integration to connections list
      if (isDemoMode && currentStep === 4 && activeSourceTab) {
        const sc = sourceConfigurations[activeSourceTab] || {} as Record<string, unknown>;
        const name = (sc.sourceName as string) || (activeSourceTab === "stripe" ? "Stripe" : activeSourceTab === "chargebee" ? "Chargebee" : activeSourceTab);
        const coreSummary = selectedPastDueHandling ? "Past Due: Remain Active" : "Past Due: Exclude";
        const advancedSummary = selectedRevenueOptions?.[1] ? `ARR: ${selectedRevenueOptions[1]}` : "ARR: MRR × 12";
        const sourceEnv = activeSourceTab === "stripe"
          ? (sc.stripeEnvironment as string) || ""
          : activeSourceTab === "chargebee"
            ? (sc.chargebeeEnvironment as string) || "Production"
            : "";
        const targetEnv = (sc.environmentName as string) || "";
        const accountIdLabel = activeSourceTab === "stripe" ? "Stripe account ID" : activeSourceTab === "chargebee" ? "Chargebee subdomain" : "Account ID";
        const accountIdValue = activeSourceTab === "stripe" ? (sc.accountId as string) || "" : (sc.subdomain as string) || "";
        const newConn = {
          id: `demo-${Date.now()}`,
          type: activeSourceTab,
          name: typeof name === "string" ? name : "Connection",
          lastSync: "Just now",
          coreSummary,
          advancedSummary,
          sourceEnvironment: sourceEnv,
          targetEnvironment: targetEnv,
          accountIdLabel,
          accountIdValue,
          syncStartDate: (sc.syncStartDate as string) || "",
          accountIdentifierKey: accountIdentifierForHealthStatus || "group_id",
          sourceCategory: (sc.sourceCategory as string) || undefined,
          sourceConfig: {
            selectedMrrPlans,
            selectedMrrStatuses,
            selectedPastDueHandling,
            selectedRevenueOptions,
            selectedChurnOptions,
            ...sc
          }
        };
        setDemoConnections(prev => {
          const next = prev.filter(c => c.type !== activeSourceTab);
          next.push(newConn);
          persistDemoConnections(next);
          return next;
        });
      }
      setCurrentStep(nextStep);
      updateUrlWithStep(nextStep);
    }
  };

  useEffect(() => {
    loadOnboardingData();
  }, []);

  // Demo-only: load persisted connections and show list when we have connections (on initial load only)
  useEffect(() => {
    if (!isDemoMode) return;
    try {
      const raw = localStorage.getItem(DEMO_CONNECTIONS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setDemoConnections(parsed);
          setViewMode("list");
          return;
        }
      }
      setDemoConnections([]);
    } catch (_) {}
  }, [isDemoMode]);

  const cleanUrlStepRef = useRef(cleanUrlStep);
  cleanUrlStepRef.current = cleanUrlStep;
  const stripeStep4CompleteFiredRef = useRef(false);

  useEffect(() => {
    return () => {
      cleanUrlStepRef.current();
    };
  }, []);

  
  useEffect(() => {
    if (currentStep !== 1) setIsNewConnectionMode(false);
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 3 && activeSourceTab && sourceConfigurations[activeSourceTab]) {
      const sourceConfig = sourceConfigurations[activeSourceTab];
      
      
      if (sourceConfig.selectedMrrPlans) {
        setSelectedMrrPlans(sourceConfig.selectedMrrPlans);
      }
      if (sourceConfig.selectedMrrStatuses) {
        setSelectedMrrStatuses(sourceConfig.selectedMrrStatuses);
      }
      if (sourceConfig.selectedPastDueHandling !== undefined) {
        setSelectedPastDueHandling(sourceConfig.selectedPastDueHandling);
      }
      if (sourceConfig.includeTax !== undefined) {
        setIncludeTax(sourceConfig.includeTax);
      }
      if (sourceConfig.baseCurrency) {
        setBaseCurrency(sourceConfig.baseCurrency);
      }
      if (sourceConfig.includeTransactionFees !== undefined) {
        setIncludeTransactionFees(sourceConfig.includeTransactionFees);
      }
      if (typeof sourceConfig.chargebeeFeePerTransaction === 'number') {
        setChargebeeFeePerTransaction(sourceConfig.chargebeeFeePerTransaction);
      }
      if (typeof sourceConfig.paymentGatewayFees === 'number') {
        setPaymentGatewayFees(sourceConfig.paymentGatewayFees);
      }
      if (typeof sourceConfig.chargebeeChargesPerTxnFee === 'boolean') {
        setChargebeeChargesPerTxnFee(sourceConfig.chargebeeChargesPerTxnFee);
      }
    }
  }, [activeSourceTab, currentStep, sourceConfigurations]);

  
  useEffect(() => {
    if (currentStep === 4 && activeSourceTab && sourceConfigurations[activeSourceTab]) {
      const sourceConfig = sourceConfigurations[activeSourceTab];
      
      
      if (sourceConfig.selectedRevenueOptions) {
        setSelectedRevenueOptions(sourceConfig.selectedRevenueOptions);
      }
      if (sourceConfig.selectedChurnOptions) {
        setSelectedChurnOptions(sourceConfig.selectedChurnOptions);
      }
    }
  }, [activeSourceTab, currentStep, sourceConfigurations]);

  useEffect(() => {
    if (currentStep === 2 && checkAtLeastOneSourceCompleted()) {
      const wasAlreadyCompleted = isDemoMode ?
        demoModeSuccessSteps.includes(2) :
        successSteps.includes(2);

      if (!wasAlreadyCompleted) {

        if (isDemoMode) {
          setDemoModeSuccessSteps(prevSteps => [...prevSteps, 2]);
        } else {
          setSuccessSteps(prevSteps => [...prevSteps, 2]);
        }
        handleAutoSaveStep2();
      }
    }

    if (currentStep === 2 && activeSourceTab === 'stripe') {
      const stripeConfig = sourceConfigurations.stripe;
      const isStripeConfigComplete = stripeConfig?.step4Completed;
      if (isStripeConfigComplete) {
        if (!stripeStep4CompleteFiredRef.current) {
          stripeStep4CompleteFiredRef.current = true;
          onStepComplete(false, undefined, true);
        }
      } else {
        stripeStep4CompleteFiredRef.current = false;
      }
    } else {
      stripeStep4CompleteFiredRef.current = false;
    }
  }, [sourceConfigurations, step2ImportResults, healthCheckCompletedStates, currentStep, activeSourceTab]);

  const handleAddNewConnection = () => {
    setEditingConnectionId(null);
    // Don't clear selectedSources — commitSelection will append the new source
    setSelectedDataType("");
    setCurrentStep(1);
    updateUrlWithStep(1);
    setViewMode("setup");
    setIsNewConnectionMode(true);
    setConnectionDraftKey(k => k + 1);
  };

  const handleConfigureConnection = (conn: ListConnection) => {
    setIsNewConnectionMode(false);
    setSelectedSources([conn.type]);
    setActiveSourceTab(conn.type);
    if (isDemoMode) {
      setEditingConnectionId(conn.id);
      if (conn.sourceConfig && conn.type) {
        setSourceConfigurations(prev => ({ ...prev, [conn.type]: { ...prev[conn.type], ...conn.sourceConfig } }));
      }
    }
    setCurrentStep(2);
    updateUrlWithStep(2);
    setViewMode("setup");
  };

  const handleOpenStepForIssue = useCallback(
    (connection: ListConnection, issue: { stepId: number; sourceType?: string }) => {
      setViewMode("setup");
      setSelectedSources([connection.type]);
      setActiveSourceTab(connection.type);
      setCurrentStep(issue.stepId);
      updateUrlWithStep(issue.stepId);
    },
    [setCurrentStep, setSelectedSources, setActiveSourceTab, updateUrlWithStep]
  );

  const handleDemoDeleteConnection = (id: string) => {
    setDemoConnections(prev => {
      const next = prev.filter(c => c.id !== id);
      persistDemoConnections(next);
      return next;
    });
  };

  const persistConfigAfterDelete = useCallback((sourceTypeToRemove: string, configsAfterRemove?: Record<string, any>) => {
    const state = store.getState().revenueAnalytics;
    const updatedConfigs = configsAfterRemove ?? (() => {
      const next = { ...state.sourceConfigurations };
      delete next[sourceTypeToRemove];
      return next;
    })();
    const newSelectedSources = (state.selectedSources || []).filter((s: string) => s !== sourceTypeToRemove);
    const newActiveSourceTab = state.activeSourceTab === sourceTypeToRemove ? (newSelectedSources[0] || "") : state.activeSourceTab;

    const isSourceCompletedForPersist = (source: string): boolean => {
      switch (source) {
        case "csv": {
          const requiredSubsteps = getRequiredSubsteps(state.selectedDataType, step2Configs);
          return requiredSubsteps.some((substepKey) => {
            const substepIdx = step2Configs.findIndex((config) => config.key === substepKey);
            return substepIdx !== -1 && (state.step2ImportResults ?? [])[substepIdx]?.success;
          });
        }
        case "api": {
          const api = updatedConfigs.api;
          const h = state.healthCheckCompletedStates || {};
          return (
            api?.status === "configured" &&
            api?.completed === true &&
            h["transactions-POST"] === true &&
            h["invoices-POST"] === true
          );
        }
        case "stripe": {
          const s = updatedConfigs.stripe;
          return (
            (s?.status === "configured" && s?.completed === true) ||
            !!(s?.step1Completed && s?.step2Completed && s?.step3Completed && s?.step4Completed)
          );
        }
        case "chargebee": {
          const c = updatedConfigs.chargebee;
          return (
            (c?.status === "configured" && c?.completed === true) ||
            !!(c?.step1Completed && c?.step2Completed && c?.step3Completed && c?.step4Completed)
          );
        }
        case "salesforce": {
          const sf = updatedConfigs.salesforce;
          return sf?.status === "configured" && sf?.completed === true;
        }
        default:
          return false;
      }
    };

    const hasAtLeastOneCompletedSource =
      newSelectedSources.length > 0 && newSelectedSources.some(isSourceCompletedForPersist);

    const dataSourceValue = {
      selectedSources: newSelectedSources,
      selectedDataType: state.selectedDataType,
      activeSourceTab: newActiveSourceTab
    };
    storeCustomerAnalyticsOnboarding({
      variables: {
        input: {
          environmentId: getEnvironmentId() ?? "",
          productId: getProductId() ?? "",
          moduleId: "revenue_analytics",
          stepId: "data_source",
          isCompleted: newSelectedSources.length > 0,
          data: JSON.stringify(dataSourceValue)
        }
      },
    });

    let apiKeyInfo = state.originalApiKeyData?.selectedApiKeyId || state.originalApiKeyData?.selectedApiKey
      ? state.originalApiKeyData
      : state.selectedApiKeyData?.selectedApiKeyId || state.selectedApiKeyData?.selectedApiKey
        ? state.selectedApiKeyData
        : undefined;
    const dataSetupValue: Record<string, unknown> = {
      selectedSources: newSelectedSources,
      selectedDataType: state.selectedDataType,
      sourceConfigurations: updatedConfigs,
      activeSourceTab: newActiveSourceTab,
      ...(apiKeyInfo && { apiKeyInfo }),
      healthCheckStates: state.healthCheckCompletedStates || {}
    };
    if (updatedConfigs.stripe && Object.keys(updatedConfigs.stripe).length > 0) {
      const s = updatedConfigs.stripe as Record<string, unknown>;
      dataSetupValue.stripeStepDetails = {
        step1Completed: !!(s.environmentId && s.stripeEnvironment),
        step2Completed: !!(s.sourceName && s.accountId && s.secretKey && s.syncStartDate),
        step3Completed: s.step3Completed ?? false,
        step4Completed: s.step4Completed ?? false,
        testCompleted: s.testCompleted ?? false,
        isStep3Incomplete: s.isStep3Incomplete ?? false,
        testingSteps: s.testingSteps ?? [],
        selectedIdentifierKey: s.selectedIdentifierKey ?? "group_id",
        customerMetaDataKeys: s.customerMetaDataKeys ?? [],
        groupIdFound: s.groupIdFound ?? false,
        formData: {
          sourceName: s.sourceName || "Stripe",
          accountId: s.accountId || "",
          secretKey: s.secretKey || "",
          dataRetentionLocation: s.dataRetentionLocation || "",
          syncStartDate: s.syncStartDate || "",
          groupIdMapping: s.groupIdMapping ?? false,
          environmentId: s.environmentId || "",
          environmentName: s.environmentName || "",
          stripeEnvironment: s.stripeEnvironment || ""
        },
        ingestionResult: s.ingestionResult ?? null,
        schemaVerified: s.schemaVerified ?? false,
        completedAt: s.completedAt ?? null,
        notificationEmails: s.notificationEmails ?? null
      };
    }
    if (updatedConfigs.chargebee && Object.keys(updatedConfigs.chargebee).length > 0) {
      const c = updatedConfigs.chargebee as Record<string, unknown>;
      dataSetupValue.chargebeeStepDetails = {
        step1Completed: !!(c.environmentId && c.chargebeeEnvironment),
        step2Completed: !!(c.sourceName && c.subdomain && c.apiKey && c.syncStartDate),
        step3Completed: c.step3Completed ?? false,
        step4Completed: c.step4Completed ?? false,
        testCompleted: c.testCompleted ?? false,
        isStep3Incomplete: c.isStep3Incomplete ?? false,
        testingSteps: c.testingSteps ?? [],
        selectedIdentifierKey: c.selectedIdentifierKey ?? "group_id",
        customerMetaDataKeys: c.customerMetaDataKeys ?? [],
        groupIdFound: c.groupIdFound ?? false,
        formData: {
          sourceName: c.sourceName || "Chargebee",
          subdomain: c.subdomain || "",
          apiKey: c.apiKey || "",
          secretKey: c.secretKey || "",
          productCatalog: c.productCatalog || "2.0",
          dataRetentionLocation: c.dataRetentionLocation || "",
          syncStartDate: c.syncStartDate || "",
          groupIdMapping: c.groupIdMapping ?? false,
          environmentId: c.environmentId || "",
          environmentName: c.environmentName || "",
          chargebeeEnvironment: c.chargebeeEnvironment || ""
        },
        ingestionResult: c.ingestionResult ?? null,
        schemaVerified: c.schemaVerified ?? false,
        completedAt: c.completedAt ?? null,
        notificationEmails: c.notificationEmails ?? null
      };
    }
    dataSetupValue.subStepsData = (state.step2ImportResults ?? []).map((_: unknown, index: number) => {
      const config = step2Configs[index];
      const importResult = (state.step2ImportResults ?? [])[index];
      const fileInfoFromStore = (state.step2FileInfos ?? [])[index];
      return {
        key: config?.key,
        title: config?.title,
        status: (state.step2SubStepStatuses ?? [])[index],
        fileInfo: fileInfoFromStore?.fileName ? { fileName: fileInfoFromStore.fileName, fileSize: fileInfoFromStore.fileSize, uploadedAt: new Date().toISOString() } : null,
        csvHeaders: (state.step2CsvHeaders ?? [])[index] || [],
        csvStatuses: index === 6 ? (state.csvDetectedStatuses ?? []) : [],
        fieldMapping: (state.step2FieldMappings ?? [])[index] || {},
        importResult: importResult ? { totalRows: importResult.totalRows || 0, processedRows: importResult.processedRows || 0, skippedRows: importResult.skippedRows || 0, duplicateRows: importResult.duplicateRows || 0, errorRows: importResult.errorRows || 0, hasErrors: !!(importResult.errors && importResult.errors.length > 0), errorCount: importResult.errors ? importResult.errors.length : 0, firstFewErrors: (importResult.errors || []).slice(0, 3), batchId: importResult.batchId || "", success: importResult.success || false, message: importResult.message || "", processingTimeMs: importResult.processingTimeMs || 0, savedAt: new Date().toISOString(), csvFileName: importResult.csvFileName || "Unknown file", originalFileSize: importResult.originalFileSize || 0 } : null
      };
    });
    dataSetupValue.completionSummary = {
      totalSubSteps: newSelectedSources.length,
      completedSubSteps: newSelectedSources.filter(isSourceCompletedForPersist).length,
      isFullyCompleted: hasAtLeastOneCompletedSource,
      autoSavedAt: new Date().toISOString(),
      hasAnyImportAttempts: (state.step2ImportResults ?? []).some((r: unknown) => r !== null)
    };

    storeCustomerAnalyticsOnboarding({
      variables: {
        input: {
          environmentId: getEnvironmentId() ?? "",
          productId: getProductId() ?? "",
          moduleId: "revenue_analytics",
          stepId: "data_setup",
          isCompleted: hasAtLeastOneCompletedSource,
          data: JSON.stringify(dataSetupValue)
        }
      }
    });

    const advanceRevenueValue: Record<string, unknown> = {
      sourceConfigurations: updatedConfigs
    };
    const hasAnyAdvanceRevenueConfigured = Object.values(updatedConfigs).some((cfg: any) => {
      if (!cfg || typeof cfg !== "object") return false;
      return (
        cfg.step4Completed === true ||
        (Array.isArray(cfg.selectedRevenueOptions) && cfg.selectedRevenueOptions.length > 0) ||
        (Array.isArray(cfg.selectedChurnOptions) && cfg.selectedChurnOptions.length > 0) ||
        cfg.advanced_revenue_calculations != null
      );
    });
    storeCustomerAnalyticsOnboarding({
      variables: {
        input: {
          environmentId: getEnvironmentId() ?? "",
          productId: getProductId() ?? "",
          moduleId: "revenue_analytics",
          stepId: "advance_revenue_calculation",
          isCompleted: hasAnyAdvanceRevenueConfigured,
          data: JSON.stringify(advanceRevenueValue)
        }
      }
    });
  }, [storeCustomerAnalyticsOnboarding]);

  const handleConfirmDeleteConnection = useCallback(() => {
    const conn = deleteModalConnection;
    if (!conn) return;
    if (isDemoMode) {
      handleDemoDeleteConnection(conn.id);
      setDeleteModalConnection(null);
      setDeleteError(null);
      return;
    }
    const environmentId = (conn.sourceConfig?.environmentId as string) || getEnvironmentId() || "";
    if (!environmentId) {
      setDeleteError("Environment not found.");
      return;
    }
    setDeleteInProgress(true);
    setDeleteError(null);
    deleteAirbyteIntegration({
      variables: {
        input: {
          environmentId,
          integrationType: conn.type === "stripe" ? IntegrationType.Stripe : conn.type === "quickbooks" ? IntegrationType.Quickbooks : IntegrationType.Chargebee
        }
      },
      onCompleted: (data) => {
        const result = data?.deleteAirbyteIntegration;
        if (result?.isSuccess) {
          const state = store.getState().revenueAnalytics;
          const updatedConfigs = { ...state.sourceConfigurations };
          delete updatedConfigs[conn.type];
          store.dispatch(setRevenueAnalyticsField({ key: "sourceConfigurations", value: updatedConfigs }));
          if (conn.type === "stripe") {
            store.dispatch(setRevenueAnalyticsField({ key: "loadedStripeData", value: null }));
          }
          setSelectedSources((prev) => prev.filter((s) => s !== conn.type));
          setActiveSourceTab((prev) => (prev === conn.type ? "" : prev));
          persistConfigAfterDelete(conn.type, updatedConfigs);
          setDeleteSuccess(true);
          setDeleteInProgress(false);
          setTimeout(() => {
            setDeleteModalConnection(null);
            setDeleteSuccess(false);
          }, 1500);
        } else {
          setDeleteError(result?.message || "Delete failed.");
          setDeleteInProgress(false);
        }
      },
      onError: (err) => {
        const message = err.message || (err.graphQLErrors?.[0]?.message) || "Failed to delete connection.";
        setDeleteError(message);
        setDeleteInProgress(false);
      }
    });
  }, [deleteModalConnection, isDemoMode, deleteAirbyteIntegration, persistConfigAfterDelete]);

  const liveConnections = useMemo((): ListConnection[] => {
    if (isDemoMode) return [];
    const configs = sourceConfigurations || {};
    const healthStates = healthCheckCompletedStates || {};
    const configKeys = Object.keys(configs);
    const selected = selectedSources || [];
    const hasAnyCsvImportAttempt = (step2ImportResults ?? []).some((r: any) => r != null);
    const sourceTypesToConsider = Array.from(
      new Set([...configKeys, ...selected, ...(hasAnyCsvImportAttempt ? ["csv"] : [])])
    );
    return sourceTypesToConsider
      .filter((sourceType) => {
        if (sourceType === "csv") {
          return hasAnyCsvImportAttempt;
        }
        const sc = configs[sourceType];
        if (sc) return isSourceVisibleInList(sourceType, configs, healthStates);
        return selected.includes(sourceType);
      })
      .map((sourceType) => {
        const sc = configs[sourceType] || {};
        const name =
          (sc.sourceName as string) ||
          (sourceType === "stripe"
            ? "Stripe"
            : sourceType === "chargebee"
              ? "Chargebee"
              : sourceType === "api"
                ? "API"
                : sourceType === "csv"
                  ? "Manual CSV Upload"
                  : sourceType);
        const sourceEnv = sourceType === "stripe" ? (sc.stripeEnvironment as string) : sourceType === "chargebee" ? (sc.chargebeeEnvironment as string) ?? "Production" : sourceType === "quickbooks" ? (sc.quickbooksEnvironment as string) ?? "" : "";
        const targetEnv = (sc.environmentName as string) || "";
        const accountIdLabel = sourceType === "stripe" ? "Stripe account ID" : sourceType === "chargebee" ? "Chargebee subdomain" : sourceType === "quickbooks" ? "Realm ID" : "Account ID";
        const accountIdValue = sourceType === "stripe" ? (sc.accountId as string) : sourceType === "chargebee" ? (sc.subdomain as string) : sourceType === "quickbooks" ? (sc.realmId as string) : "";
        const ingestionSyncAt = (sc.ingestionResult as { syncStartedAt?: string })?.syncStartedAt;
        const completedAt = sc.completedAt as string | undefined;
        const lastSync = ingestionSyncAt ?? completedAt ?? "";
        return {
          id: sourceType,
          type: sourceType,
          name,
          lastSync,
          sourceEnvironment: sourceEnv,
          targetEnvironment: targetEnv,
          accountIdLabel,
          accountIdValue,
          syncStartDate: (sc.syncStartDate as string) || "",
          accountIdentifierKey: (sc.selectedIdentifierKey as string) || "group_id",
          sourceConfig: sc as Record<string, unknown>,
          sourceCategory: (sc.sourceCategory as string) || undefined,
        };
      });
  }, [isDemoMode, sourceConfigurations, healthCheckCompletedStates, isSourceVisibleInList, selectedSources, step2ImportResults]);

  const listConnections: ListConnection[] = isDemoMode ? demoConnections : liveConnections;

  const listConnectionsWithStatus = useMemo(() => {
    const configs = sourceConfigurations || {};
    const healthStates = healthCheckCompletedStates || {};
    return listConnections.map((conn) => {
      const isComplete = isSourceConfiguredForList(conn.type, configs, healthStates);
      const status = isComplete ? ("active" as const) : ("incomplete" as const);
      const issues: Array<{ id: string; label: string; stepId: number; sourceType?: string }> = [];

      const dataSetupIncomplete = !checkStep2CompletionForSource(conn.type);
      if (dataSetupIncomplete) {
        issues.push({ id: `data-setup-${conn.id}`, label: "Data setup incomplete", stepId: 2, sourceType: conn.type });
      }
      if (!successSteps.includes(3)) {
        issues.push({ id: `core-revenue-${conn.id}`, label: "Core revenue calculation incomplete", stepId: 3 });
      }
      if (!successSteps.includes(4)) {
        issues.push({ id: `advance-revenue-${conn.id}`, label: "Advanced revenue calculation incomplete", stepId: 4 });
      }
      if (!successSteps.includes(5)) {
        issues.push({ id: `set-goals-${conn.id}`, label: "Set goals incomplete", stepId: 5 });
      }
      if (conn.type === "stripe" || conn.type === "chargebee") {
        const sourceLabel = conn.type === "stripe" ? "Stripe" : "Chargebee";
        const hasIdentifierKey = !!(conn.sourceConfig as Record<string, unknown>)?.selectedIdentifierKey;
        if (!hasIdentifierKey) {
          issues.push({
            id: `${SETUP_HEALTH_ISSUE_IDS.MISSING_ACCOUNT_IDENTIFIER}-${conn.id}`,
            label: getConfigureAccountIdentifierLabel(sourceLabel),
            stepId: 7
          });
        }
        if (idMissingAccountsCount > 0) {
          issues.push({ id: `id-missing-accounts-${conn.id}`, label: SETUP_HEALTH_ISSUE_LABELS[SETUP_HEALTH_ISSUE_IDS.ID_MISSING_ACCOUNTS], stepId: 7 });
        }
      }
      if (missingRevenueDataCount > 0) {
        issues.push({ id: `missing-revenue-data-${conn.id}`, label: SETUP_HEALTH_ISSUE_LABELS[SETUP_HEALTH_ISSUE_IDS.MISSING_REVENUE_DATA], stepId: 7 });
      }
      if (transactionsWithoutInvoiceCount > 0) {
        issues.push({ id: `transactions-without-invoices-${conn.id}`, label: SETUP_HEALTH_ISSUE_LABELS[SETUP_HEALTH_ISSUE_IDS.TRANSACTIONS_WITHOUT_INVOICES], stepId: 7 });
      }
      if (conn.type === "api") {
        const hasHealthError = Object.keys(healthStates).some((k) => healthStates[k] !== true);
        if (hasHealthError) {
          issues.push({ id: `api-health-${conn.id}`, label: SETUP_HEALTH_ISSUE_LABELS[SETUP_HEALTH_ISSUE_IDS.API_HEALTH], stepId: 7 });
        }
      }

      const issueCount = issues.length;
      const hasError = issueCount > 0;
      return { ...conn, status, hasError, isComplete, issueCount, issues };
    });
  }, [
    listConnections,
    successSteps,
    sourceConfigurations,
    healthCheckCompletedStates,
    idMissingAccountsCount,
    missingRevenueDataCount,
    transactionsWithoutInvoiceCount,
    isSourceConfiguredForList,
    checkStep2CompletionForSource,
  ]);

  const showListView = viewMode === "list";
  const isEditFromList =
    !showListView &&
    listConnections.length > 0 &&
    selectedSources.length === 1 &&
    listConnections.some((c) => c.type === selectedSources[0]);
  const isStep1Locked = isEditFromList;

  const deepLinkAutoOpenFiredRef = useRef(false);
  useEffect(() => {
    if (deepLinkAutoOpenFiredRef.current) return;
    if (!showListView) return;

    const stepParam = searchParams.get("step");
    const substepParam = searchParams.get("substep");
    if (!stepParam || !substepParam) return;

    const parsedStep = parseInt(stepParam, 10);
    const parsedSubstep = parseInt(substepParam, 10);
    if (Number.isNaN(parsedStep) || Number.isNaN(parsedSubstep)) return;

    const firstActive = listConnectionsWithStatus.find((c) => c.status === "active");
    if (!firstActive) return;

    deepLinkAutoOpenFiredRef.current = true;

    setViewMode("setup");
    setSelectedSources([firstActive.type]);
    setActiveSourceTab(firstActive.type);
    setCurrentStep(parsedStep);
    updateUrlWithStep(parsedStep);

    const timer = window.setTimeout(() => {
      cleanUrlStepAndSubstep();
    }, 800);

    return () => window.clearTimeout(timer);
  }, [
    showListView,
    searchParams,
    listConnectionsWithStatus,
    setSelectedSources,
    setActiveSourceTab,
    setCurrentStep,
    setViewMode,
    updateUrlWithStep,
    cleanUrlStepAndSubstep,
  ]);

  // List view (table) – same layout as SetupCRM when setup is completed
  if (showListView) {
    return (
      <div className="min-h-screen bg-primaryExtremeLightColor w-screen">
        <div className="relative min-h-screen bg-gray-50 w-[70%] mx-auto overflow-hidden">
          <div className="flex flex-col px-8 py-4 gap-4">
            <div className="flex">
              <button
                onClick={() => navigate("/onboarding")}
                className="w-fit p-0 min-w-0 text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>
            <SetupHeader
              icon={<DollarSign className="w-8 h-8 text-primaryColor" />}
              title="Setup Revenue Intelligence"
              description="Track and analyze your revenue metrics to optimize monetization and drive growth."
              teams={[TeamId.SALES_TEAMS, TeamId.REVOPS_TEAMS]}
              setupTime={20}
              totalSteps={steps.length}
              completedSteps={isDemoMode ? demoModeSuccessSteps.length : successSteps.length}
              onInviteTeam={handleInviteTeam}
              headerAction={
                <button
                  onClick={revenueNotificationPrefs.open}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <Bell className="w-4 h-4" />
                  Notifications
                </button>
              }
            />
          </div>
          <div className="flex gap-8 ms-3 py-4 px-8 h-[calc(100vh-200px)] overflow-y-auto">
            <div className="flex-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <RevenueConnectionsList
                  connections={listConnectionsWithStatus}
                  onAddNew={handleAddNewConnection}
                  onEdit={handleConfigureConnection}
                  onDelete={(conn) => {
                    setDeleteModalConnection(conn);
                    setDeleteError(null);
                    setDeleteSuccess(false);
                  }}
                  canAddNew={true}
                  canDelete={(conn) => isDemoMode || conn.type === "stripe" || conn.type === "chargebee" || conn.type === "quickbooks"}
                  onOpenSetupHealthStatus={() => {
                    setViewMode("setup");
                    setCurrentStep(7);
                    updateUrlWithStep(7);
                  }}
                  onOpenStepForIssue={handleOpenStepForIssue}
                  leftContent={<EnvironmentSelector />}
                />
              </div>
            </div>
          </div>
          {deleteModalConnection && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900">Delete Connection?</h3>
                      <p className="mt-2 text-sm text-gray-600">
                        Are you sure you want to delete <strong>{deleteModalConnection.name}</strong>? This action <strong className="text-red-600 underline">cannot be undone</strong> and will permanently delete the connection and <strong>all synced revenue data.</strong>
                      </p>
                    </div>
                  </div>
                  {(deleteError || deleteSuccess || (deleteInProgress || deleteIntegrationLoading)) && (
                    <div className="flex gap-4 mt-4">
                      <div className="flex-shrink-0 w-10" aria-hidden />
                      <div className="flex-1 min-w-0 space-y-0">
                        {deleteError && (
                          <div className="p-3 rounded-lg bg-gray-50 text-sm text-red-700">
                            {deleteError}
                          </div>
                        )}
                        {deleteSuccess && (
                          <div className="p-3 rounded-lg bg-gray-50 text-sm text-green-700 font-medium">
                            Complete
                          </div>
                        )}
                        {(deleteInProgress || deleteIntegrationLoading) && !deleteSuccess && (
                          <div className="p-3 rounded-lg bg-gray-50 text-sm text-blue-700 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin text-blue-600" aria-hidden />
                            Deleting connection and Data…
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => { setDeleteModalConnection(null); setDeleteError(null); setDeleteSuccess(false); }}
                      disabled={deleteInProgress || deleteIntegrationLoading}
                      className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmDeleteConnection}
                      disabled={deleteInProgress || deleteIntegrationLoading}
                      className="px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm & Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {selectedTeam && showInviteModal && (
            <InviteTeamModal
              isOpen={showInviteModal}
              onClose={() => setShowInviteModal(false)}
              team={selectedTeam ?? undefined}
              onInvite={() => { }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center w-screen overflow-y-auto bg-gradient-to-br bg-primaryExtremeLightColor dark:from-gray-900 dark:to-primaryDarkColor transition-colors duration-200">
      <div className="min-h-screen bg-gray-50 w-[85%] mx-auto px-8 pt-8 relative">
        <div className="flex py-4">
          <button
            onClick={() => {
              if (viewMode === "setup" && isStepCompleted(1)) {
                setViewMode("list");
              } else {
                navigate("/onboarding");
              }
            }}
            className="w-fit p-0 min-w-0 text-gray-600 hover:text-gray-900 flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
            {viewMode === "setup" && isStepCompleted(1) ? "Back to Integrations" : null}
          </button>
        </div>

        <div>
        <div className="flex gap-6 overflow-y-auto max-h-[calc(100vh-140px)]">
          {/* Left Navigation */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky bg-gray-50 px-4 pb-4">
              
              <div className="mb-6 pb-4 border-b border-gray-200">

                <div className="flex flex-col gap-1 w-full">
                  <label className="font-medium text-gray-600">Select Environment</label>
                  <EnvironmentSelector />
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 px-2">
                  Basic Setup
                </h3>
                <div className="space-y-0">
                  {steps.slice(0, 3).map((step, index) => {
                    const completed = isStepCompleted(step.id);
                    const isActive = step.id === currentStep;
                    const isLocked = step.id === 1 && isStep1Locked;

                    const handleStepClick = () => {
                      if (isLocked) return;
                      setCurrentStep(step.id);
                      updateUrlWithStep(step.id);
                    };

                    return (
                      <div key={step.id} className={`relative ${isLocked ? "opacity-60" : ""}`}>
                        <div
                          className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200
                            ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}
                            ${isActive 
                              ? "bg-blue-100" 
                              : completed 
                                ? "hover:bg-blue-50" 
                                : "hover:bg-blue-50"
                            }`}
                          onClick={handleStepClick}
                          role={isLocked ? "button" : undefined}
                          aria-disabled={isLocked}>
                          <div className="relative flex flex-col items-center">
                            <div
                              className={`z-10 flex items-center justify-center rounded-full transition-all duration-200 p-2
                              ${completed
                                  ? "bg-[#22c55e] text-white"
                                  : isActive
                                    ? "bg-primaryColor text-white"
                                    : "bg-gray-100 text-gray-400"
                                }`}>
                              {completed ? <CheckCircle2 className="w-5 h-5" /> : step.icon}
                            </div>
                          </div>
                          <div className="flex items-center flex-1">
                            <span className={`text-sm font-medium ${isActive ? "text-primaryColor" : "text-gray-700"}`}>
                              {step.title}
                            </span>
                            {step?.comingSoon && <PlannedPill />}
                          </div>
                        </div>
                        {index < steps.slice(0, 3).length - 1 && (
                          <div className="flex justify-start ml-4">
                            <div className="w-[2px] h-6 bg-gray-200 ml-3"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 px-2">
                  Advanced Setup
                </h3>
                <div className="space-y-0">
                  {steps.slice(3).filter(step => step.id !== 7).map((step, index) => {
                    const completed = isStepCompleted(step.id);
                    const isActive = step.id === currentStep;

                    const handleStepClick = () => {
                      setCurrentStep(step.id);
                      updateUrlWithStep(step.id);
                    };

                    return (
                      <div key={step.id} className="relative">
                        <div
                          className={`flex items-center space-x-3 px-3 py-3 rounded-lg cursor-pointer transition-all duration-200
                            ${isActive 
                              ? "bg-gray-200" 
                              : completed 
                                ? "hover:bg-gray-50" 
                                : "hover:bg-gray-50"
                            }`}
                          onClick={handleStepClick}>
                          <div className="relative flex flex-col items-center">
                            <div
                              className={`z-10 flex items-center justify-center rounded-full transition-all duration-200 p-2
                              ${completed
                                  ? "bg-[#22c55e] text-white"
                                  : isActive
                                    ? "bg-primaryColor text-white"
                                    : "bg-gray-100 text-gray-400"
                                }`}>
                              {completed ? <CheckCircle2 className="w-5 h-5" /> : step.icon}
                            </div>
                          </div>
                          <div className="flex items-center flex-1">
                            <span className={`text-sm font-medium ${isActive ? "text-primaryColor" : "text-gray-700"}`}>
                              {step.title}
                            </span>
                            {step?.comingSoon && <PlannedPill />}
                          </div>
                        </div>
                        {index < steps.slice(3).length - 1 && (
                          <div className="flex justify-start ml-4">
                            <div className="w-[2px] h-6 bg-gray-200 ml-3"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 px-2">
                  Diagnostics
                </h3>
                <div className="space-y-0">
                  {steps.slice(6).map((step, index) => {
                    const completed = isStepCompleted(step.id);
                    const isActive = step.id === currentStep;

                    const handleStepClick = () => {
                      setCurrentStep(step.id);
                      updateUrlWithStep(step.id);
                    };

                    return (
                      <div key={step.id} className="relative">
                        <div
                          className={`flex items-center space-x-3 px-3 py-3 rounded-lg cursor-pointer transition-all duration-200
                            ${isActive 
                              ? "bg-gray-200" 
                              : completed 
                                ? "hover:bg-gray-50" 
                                : "hover:bg-gray-50"
                            }`}
                          onClick={handleStepClick}>
                          <div className="relative flex flex-col items-center">
                            <div
                              className={`z-10 flex items-center justify-center rounded-full transition-all duration-200 p-2
                              ${completed
                                  ? "bg-[#22c55e] text-white"
                                  : isActive
                                    ? "bg-primaryColor text-white"
                                    : "bg-gray-100 text-gray-400"
                                }`}>
                              {completed ? <CheckCircle2 className="w-5 h-5" /> : step.icon}
                            </div>
                          </div>
                          <div className="flex items-center flex-1">
                            <span className={`text-sm font-medium ${isActive ? "text-primaryColor" : "text-gray-700"}`}>
                              {step.title}
                            </span>
                            {step?.comingSoon && <PlannedPill />}
                          </div>
                        </div>
                        {index < steps.slice(6).length - 1 && (
                          <div className="flex justify-start ml-4">
                            <div className="w-[2px] h-6 bg-gray-200 ml-3"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-auto scrollbar-hide">
            <div className="mb-6">
              <SetupHeader
                icon={<DollarSign className="w-8 h-8 text-primaryColor" />}
                title="Setup Revenue Intelligence"
                description="Track and analyze your revenue metrics to optimize monetization and drive growth."
                teams={[TeamId.SALES_TEAMS, TeamId.REVOPS_TEAMS]}
                setupTime={20}
                totalSteps={steps.length}
                completedSteps={isDemoMode ? demoModeSuccessSteps.length : successSteps.length}
                onInviteTeam={handleInviteTeam}
                headerAction={
                  <button
                    onClick={revenueNotificationPrefs.open}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <Bell className="w-4 h-4" />
                    Notifications
                  </button>
                }
              />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 pb-32 w-full">
              <div className="space-y-6">
                {currentStep === 1 && (
                  <DataCollectionMethod
                    ref={dataModelRef}
                    dataCollectionOptions={dataCollectionOptions}
                    getColorClasses={getColorClasses}
                    getIconColor={getIconColor}
                    loading={!isReady}
                    isSchemaDrawerOpen={isSchemaDrawerOpen}
                    openSchemaModal={openSchemaModal}
                    openSchemaDrawer={openSchemaDrawer}
                closeAllModals={closeAllModals}
                forceReset={isNewConnectionMode}
                connectionDraftKey={connectionDraftKey}
                alreadyConfiguredSourceIds={Array.from(
                  new Set(listConnectionsWithStatus.filter(c => c.isComplete).map((c) => c.type))
                )}
                  />

                )}
                {currentStep === 2 && (
                  <div className="w-full">
                    <div className="flex items-start space-x-3 mb-5">
                      <div className="p-2 bg-primaryExtremeLightColor rounded-lg mt-2">
                        {selectedSources.length === 1 && selectedSources[0] === 'stripe' ? (
                          <SiStripe className="w-6 h-6 text-primaryColor" />
                        ) : selectedSources.length === 1 && selectedSources[0] === 'chargebee' ? (
                          <img src={ChargebeeIcon} alt="Chargebee" className="w-6 h-6" style={{ objectFit: 'contain' }} />
                        ) : selectedSources.length === 1 && selectedSources[0] === 'quickbooks' ? (
                          <SiQuickbooks className="w-6 h-6 text-[#2CA01C]" />
                        ) : (
                          <Upload className="w-6 h-6 text-primaryColor" />
                        )}
                      </div>
                      <div className="flex items-center w-full mb-2">
                        <div className="flex flex-col justify-between flex-1">
                          <h2 className="text-2xl font-bold text-gray-900 mb-1">
                            {selectedSources.length === 1
                              ? `Configure ${selectedSources[0].charAt(0).toUpperCase() + selectedSources[0].slice(1)}`
                              : 'Configure Data Sources'}
                          </h2>

                          {/* Description */}
                          {selectedSources.length === 1 && (selectedSources[0] === 'stripe' || selectedSources[0] === 'chargebee' || selectedSources[0] === 'quickbooks') ? (
                            <p className="text-gray-600">
                              Configure your {selectedSources[0].charAt(0).toUpperCase() + selectedSources[0].slice(1)} data source connection
                            </p>
                          ) : (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-sm text-gray-500">
                                {getCompletedSourcesCount()} of {selectedSources.length} source{selectedSources.length !== 1 ? 's' : ''} completed
                              </span>
                              {checkAtLeastOneSourceCompleted() && (
                                <span className="text-xs bg-green-50 text-green-800 px-2 py-1 rounded-full font-medium">
                                  Ready to proceed
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className='flex ml-auto gap-6 items-center'>
                          {/* Progress Bar - show only for single Stripe/Chargebee/QuickBooks source */}
                          {selectedSources.length === 1 && (selectedSources[0] === 'stripe' || selectedSources[0] === 'chargebee' || selectedSources[0] === 'quickbooks') && (() => {
                            const source = selectedSources[0];
                            const config = sourceConfigurations[source];
                            let completedSteps = 0;
                            let totalSteps = 4;

                            if (config) {
                              if (source === 'stripe' || source === 'chargebee') {
                                if (config.step1Completed || (config.environmentId && config[source === 'stripe' ? 'stripeEnvironment' : 'chargebeeEnvironment'])) completedSteps++;
                                if (config.step2Completed || (config.sourceName && config.apiKey)) completedSteps++;
                                if (config.step3Completed) completedSteps++;
                                if (config.step4Completed) completedSteps++;
                              } else if (source === 'quickbooks') {
                                if (config.step1Completed) completedSteps++;
                                if (config.step2Completed) completedSteps++;
                                if (config.step3Completed) completedSteps++;
                                if (config.step4Completed) completedSteps++;
                              }
                            }

                            const progressPercentage = (completedSteps / totalSteps) * 100;

                            return (
                              <div className="flex items-center space-x-3">
                                <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progressPercentage}%` }}
                                  />
                                </div>
                                <div className="text-sm font-medium text-gray-700">
                                  {completedSteps}/{totalSteps} completed ({Math.round(progressPercentage)}%)
                                </div>
                              </div>
                            );
                          })()}

                          {/* Help Guide Button - show only for single Stripe/Chargebee/QuickBooks source */}
                          {selectedSources.length === 1 && (selectedSources[0] === 'stripe' || selectedSources[0] === 'chargebee' || selectedSources[0] === 'quickbooks') && (
                            <button
                              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                              onClick={() => {
                                if (openSetupGuideRef.current) {
                                  openSetupGuideRef.current();
                                }
                              }}
                            >
                              <BookOpen className="h-4 w-4" />
                              Setup Guide
                            </button>
                          )}

                          {/* Dev Guide - hide for single Stripe/Chargebee/QuickBooks source */}
                          {!(selectedSources.length === 1 && (selectedSources[0] === 'stripe' || selectedSources[0] === 'chargebee' || selectedSources[0] === 'quickbooks')) && (
                            <button
                              onClick={openSchemaModal}
                              className="text-blue-600 hover:text-blue-800 underline hover:underline-offset-2 bg-transparent px-0 py-0 text-sm font-medium transition-colors flex items-center gap-1"
                            >
                              <Database className="w-4 h-4" />
                              <span>Dev Guide: Data Model Schema</span>
                            </button>
                          )}
                          {selectedSources.includes('csv') && (
                            <button
                              onClick={handleDownloadAllSamples}
                              className="flex items-center space-x-2 h-10 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download All Sample Files</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <DataSetup
                      step2Configs={modifiedStep2Configs}
                      step2UploadedFiles={step2UploadedFiles}
                      mapFieldsRefs={mapFieldsRefs}
                      nextButtonRefs={nextButtonRefs}
                      handleStep2FileUpload={handleStep2FileUpload}
                      handleStep2FieldMappingChange={handleStep2FieldMappingChange}
                      processStep2Import={processStep2Import}
                      handleDownloadAllSamples={handleDownloadAllSamples}
                      downloadFailedRecords={downloadFailedRecords}
                      fileInputRef={fileInputRef}
                      getStepStatus={getStepStatus}
                      renderImportResults={renderImportResults}
                      getStatusDisplay={getStatusDisplay}
                      getImportButtonText={getImportButtonText}
                      retryStep2Import={retryStep2Import}
                      onStepComplete={onStepComplete}
                      openSetupGuideRef={openSetupGuideRef}
                      onAutoSave={handleAutoSaveStep2}
                      onTrackCompletion={trackDataSetupCompletion}
                      onApiKeyDataChange={(data) => {
                        setSelectedApiKeyData(data);
                        setApiKeyChanged(true);
                        setApiKeyMarkedComplete(false);
                      }}
                      initialApiKeyData={selectedApiKeyData}
                      emailManager={emailManager}
                      cleanOAuthParams={cleanOAuthParamsFromUrl}
                      onOpenIntegrationsList={() => setViewMode("list")}
                    />
                    <SchemaDrawer
                      isOpen={isSchemaDrawerOpen}
                      onClose={closeAllModals}
                      onOpenSchemaModal={openSchemaModal}
                    />
                  </div>
                )}
                <DatasetSchemaModal
                  isOpen={isSchemaModalOpen}
                  onClose={closeAllModals}
                  onOpenSchemaDrawer={openSchemaDrawer}
                />
                {currentStep === 3 && (
                  <div className="w-full">
                    <div className="flex items-start space-x-3 mb-5">
                      <div className="p-2 bg-primaryExtremeLightColor rounded-lg">
                        <TrendingUp className="w-6 h-6 text-primaryColor" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-1">
                          Core Revenue Calculation
                        </h2>
                        <p className="text-gray-600">
                          Setup the core revenue calculation for your business.
                        </p>
                      </div>
                    </div>
                    <SetupMRR
                      includeTransactionFees={includeTransactionFees}
                      setIncludeTransactionFees={setIncludeTransactionFees}
                      step2UploadedFiles={step2UploadedFiles}
                      step2CsvHeaders={step2CsvHeaders}
                      step2SampleRows={step2SampleRows}
                      onComplete={(plans, statuses, pastDue, includeTax, currency, includeTransactionFees) => {
                        setSelectedMrrPlans(plans);
                        setSelectedMrrStatuses(statuses);
                        setSelectedPastDueHandling(pastDue);
                        setIncludeTax(includeTax);
                        setBaseCurrency(currency);
                        setIncludeTransactionFees(includeTransactionFees)
                      }}
                      loadedMrrPlans={selectedMrrPlans}
                      loadedMrrStatuses={selectedMrrStatuses}
                      loadedPastDueHandling={selectedPastDueHandling}
                      onGoToDataSetup={() => {
                        setCurrentStep(2);
                        updateUrlWithStep(2);
                      }}
                      statusOptions={csvDetectedStatuses}
                      setCsvDetectedStatuses={setCsvDetectedStatuses}
                      dataSetupIsComplete={checkStep2Completion()}
                      selectedDataType={selectedDataType}
                      setIsMrrCompleted={setIsMrrCompleted}
                      isMrrCompleted={isMrrCompleted}
                      includeTax={includeTax}
                      onStepComplete={onStepComplete}
                      selectedSources={selectedSources[0]}
                      chargebeeFeePerTransaction={chargebeeFeePerTransaction}
                      setChargebeeFeePerTransaction={setChargebeeFeePerTransaction}
                      paymentGatewayFees={paymentGatewayFees}
                      setPaymentGatewayFees={setPaymentGatewayFees}
                      paymentGatewayType={paymentGatewayType}
                      setPaymentGatewayType={setPaymentGatewayType}
                      selectedPaymentGateway={selectedPaymentGateway}
                      setSelectedPaymentGateway={setSelectedPaymentGateway}
                      chargebeeChargesPerTxnFee={chargebeeChargesPerTxnFee}
                      setChargebeeChargesPerTxnFee={setChargebeeChargesPerTxnFee}
                      activeSourceTab={activeSourceTab}
                    />
                  </div>
                )}
                {currentStep === 4 && (
                  <div className="w-full">
                    <div className="flex items-start space-x-3 mb-5">
                      <div className="p-2 bg-primaryExtremeLightColor rounded-lg">
                        <TrendingUp className="w-6 h-6 text-primaryColor" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-1">
                          Advance Revenue Calculation
                        </h2>
                        <p className="text-gray-600">
                          Setup the advance revenue calculation for your business.
                        </p>
                      </div>
                    </div>
                    <SetupARR
                      onComplete={(revenueOptions, churnOptions) => {
                        setSelectedRevenueOptions(revenueOptions);
                        setSelectedChurnOptions(churnOptions);
                      }}
                      loadedRevenueOption={selectedRevenueOptions[0]}
                      loadedArrMethod={selectedRevenueOptions[1]}
                      loadedChurnOptions={selectedChurnOptions}
                    />
                  </div>
                )}
                {currentStep === 5 && (
                  <div className="w-full">
                    <div className="flex items-start mb-5 gap-3">
                      <div className="p-2 bg-primaryExtremeLightColor rounded-lg">
                        <Goal className="w-6 h-6 text-primaryColor" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-1">
                          Set Goals
                        </h2>
                        <p className="text-gray-600">
                          Configure revenue goals and targets to track your business progress.
                        </p>
                      </div>
                      {/* <div className='flex ml-auto'>
                        <div className="flex space-x-3">
                          <button
                            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                          >
                            <Save className="w-4 h-4" />
                            <span>{'Save Goals'}</span>
                          </button>
                        </div>
                      </div> */}
                    </div>
                    <div className="pb-20 ms-9">
                      <GoalSetupModal
                        currentGoals={goals as Goals}
                        onSave={(newGoals) => {
                          setGoals(newGoals);
                        }}
                        currency={baseCurrency}
                        asModal={false}
                        isDemoMode={isDemoMode}
                        onGoalsChange={handleStep5GoalsChange}
                        onPersistOnboardingStep={commitSetGoalsOnboardingState}
                      />
                    </div>
                  </div>
                )}
                {currentStep === 6 && (
                  <div className="space-y-6">
                    <CommingSoonComponent
                      mode="email"
                      heading="Revenue Alerts Features Coming Soon! 🚀"
                      description="We're building a revenue alert system to help you track your revenue and take action when needed."
                      icon="lock"
                      customEmailSubject="Requesting Revenue Alerts"
                      requestLabel="Request Revenue Alerts"
                    />
                    <SetupRevenueAlerts />
                  </div>
                )}
                {currentStep === 7 && (
                  <div className="space-y-6 flex-1 overflow-x-hidden">
                    {/* Header */}
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-primaryExtremeLightColor rounded-lg">
                        <Gauge className="w-6 h-6 text-primaryColor" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <h2 className="text-xl font-semibold text-gray-910 mb-1">
                            Setup Health Status
                          </h2>
                          <button
                            onClick={() => setIsHealthStatusGuideOpen(true)}
                            className="flex ml-auto items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <BookOpen className="w-4 h-4" />
                            Guide
                          </button>
                        </div>
                        <p className="text-gray-600">
                          Monitor and resolve data quality issues to ensure accurate analytics and reporting.
                        </p>
                      </div>
                    </div>

                    {/* Diagnostic Issue Cards - Always show on Setup Health Status step */}
                    {(
                      <div className="space-y-3">
                        {/* Step 1: Configure Account Identifier - for Stripe or Chargebee (one block, switches by source) */}
                        {(() => {
                          const hasStripeOrChargebee = selectedSources.includes('stripe') || selectedSources.includes('chargebee');
                          const displaySource = (activeSourceTab === 'stripe' || activeSourceTab === 'chargebee') && selectedSources.includes(activeSourceTab)
                            ? activeSourceTab
                            : selectedSources.includes('stripe')
                              ? 'stripe'
                              : 'chargebee';
                          const config = displaySource === 'chargebee' ? sourceConfigurations.chargebee : sourceConfigurations.stripe;
                          const sourceLabel = displaySource === 'chargebee' ? 'Chargebee' : 'Stripe';
                          const isConfigured = !!(config?.selectedIdentifierKey);
                          const metaDataKeys = config?.customerMetaDataKeys ?? [];
                          const hasNoKeys = !isDemoMode && metaDataKeys.length === 0;
                          if (!hasStripeOrChargebee) return null;
                          return (
                            <div
                              id={
                                displaySource === "stripe"
                                  ? "missing-account-identifier-stripe"
                                  : "missing-account-identifier-chargebee"
                              }
                              className="border border-gray-200 rounded-lg bg-white shadow-sm">
                              <button
                                onClick={() => setExpandedAccountIdentifierSection(!expandedAccountIdentifierSection)}
                                className="w-full flex items-center p-5 hover:bg-gray-50 transition-colors relative"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="flex items-center justify-center w-8 h-8 rounded-full text-white font-semibold flex-shrink-0 bg-gray-700">
                                    <span className="text-sm">1</span>
                                  </div>
                                  <div className="text-left">
                                    <div className="text-lg font-semibold text-gray-900">{getConfigureAccountIdentifierLabel(sourceLabel)}</div>
                                    <div className="text-sm text-gray-600">
                                      Account identifier is the unique key in your product telemetry for customers that link them to a Account. Choose a key from your {sourceLabel} customer metadata to link account records.
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  {isConfigured ? (
                                    <span className="text-sm font-semibold text-green-600">Configured</span>
                                  ) : (
                                    <span className="text-sm font-semibold text-red-600">Not configured</span>
                                  )}
                                  {expandedAccountIdentifierSection ? (
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                  )}
                                </div>
                              </button>

                              {expandedAccountIdentifierSection && (
                                <div className="px-5 pb-5 border-t border-gray-200 pt-5 bg-gray-50 pl-12">
                                  {hasNoKeys ? (
                                    <>
                                      <p className="text-sm text-gray-600">
                                        <span className="text-base font-bold text-gray-800">Error: </span>No account identifier key is found in the customer data sent by {sourceLabel}.
                                      </p>
                                      <div className="space-y-2">
                                        <div>
                                          <span className="font-bold text-gray-800">Impact: </span>
                                          <span className="text-sm text-gray-600">
                                            Essential for accurately linking records across revenue, marketing, and product—ensuring every account&apos;s activities, transactions, and campaigns are unified under one identifier.
                                          </span>
                                        </div>
                                        <div className="flex items-center">
                                          <span className="font-bold text-gray-800">Resolution: </span>
                                          <span className="text-sm text-gray-600">Include an Account identifier key in {sourceLabel}&apos;s metadata.</span>
                                          <button
                                            onClick={() => {
                                              setSetupGuideSource(displaySource);
                                              setIsSetupGuideOpen(true);
                                              setTimeout(() => {
                                                if (setupGuideStep3Ref.current) {
                                                  setupGuideStep3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }
                                              }, 100);
                                            }}
                                            className="text-sm text-blue-600 hover:underline ms-1"
                                          >
                                            See how to fix this
                                          </button>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div>
                                        <div className="flex items-center gap-4">
                                          <label className="block font-bold text-gray-800">
                                            Current selected Account Identifier:
                                          </label>
                                          <select
                                            value={displaySource === 'stripe' ? accountIdentifierForHealthStatus : (config?.selectedIdentifierKey ?? 'group_id')}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              if (displaySource === 'stripe') {
                                                setAccountIdentifierForHealthStatus(value);
                                                if (sourceConfigurations.stripe) {
                                                  updateSourceConfiguration('stripe', {
                                                    ...sourceConfigurations.stripe,
                                                    selectedIdentifierKey: value
                                                  });
                                                }
                                              } else if (sourceConfigurations.chargebee) {
                                                updateSourceConfiguration('chargebee', {
                                                  ...sourceConfigurations.chargebee,
                                                  selectedIdentifierKey: value
                                                });
                                              }
                                            }}
                                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          >
                                            {isDemoMode ? (
                                              <option value="group_id">group_id</option>
                                            ) : (
                                              (metaDataKeys.length ? metaDataKeys : ['group_id']).map((key: string) => (
                                                <option key={key} value={key}>
                                                  {key}
                                                </option>
                                              ))
                                            )}
                                          </select>
                                        </div>
                                      </div>
                                      <div className="space-y-2 mt-4">
                                        <div>
                                          <span className="font-bold text-gray-800">Impact: </span>
                                          <span className="text-sm text-gray-600">
                                            Customers without this key are excluded from account-level linking — This breaks the correlation between marketing, sales and revenue.
                                          </span>
                                        </div>
                                        <div className="flex items-center">
                                          <span className="font-bold text-gray-800">Resolution: </span>
                                          <span className="text-sm text-gray-600 ms-1">
                                            If account identifier key used in your product telemetry is not present in the list —{' '}
                                            <button
                                              onClick={() => {
                                                setSetupGuideSource(displaySource);
                                                setIsSetupGuideOpen(true);
                                                setTimeout(() => {
                                                  if (setupGuideStep3Ref.current) {
                                                    setupGuideStep3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                  }
                                                }, 100);
                                              }}
                                              className="text-blue-600 hover:underline"
                                            >
                                              see how to fix this
                                            </button>
                                          </span>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Revenue Data with Missing Account Identifier */}
                        <DiagnosticIssueCards
                          expandedIssueId={expandedIssueId}
                          onToggleIssue={setExpandedIssueId}
                          idMissingAccountsCount={idMissingAccountsCount}
                          missingRevenueDataCount={missingRevenueDataCount}
                          unidentifiedAccountsCount={unidentifiedAccountsCount}
                          unidentifiedFromRevenueCount={unidentifiedFromRevenueCount}
                          unidentifiedFromProductCount={unidentifiedFromProductCount}
                          selectedSource={selectedSources[0] as 'stripe' | 'api' | 'csv' | null || null}
                          accountIdentifier="group_id"
                          filteredIssueIds={['id-missing-accounts']}
                          hideExportImport={true}
                          showRedirectLink={true}
                          redirectLink={`/analyze/diagnostics-report/revenue-product-correlation?subIssueId=id-missing-accounts`}
                          redirectLinkText="View detailed resolution steps"
                          displayIndex={(selectedSources.includes('stripe') || selectedSources.includes('chargebee')) ? 2 : 1}
                        />

                        {/* Accounts Without Revenue Data */}
                        <DiagnosticIssueCards
                          expandedIssueId={expandedIssueId}
                          onToggleIssue={setExpandedIssueId}
                          idMissingAccountsCount={idMissingAccountsCount}
                          missingRevenueDataCount={missingRevenueDataCount}
                          unidentifiedAccountsCount={unidentifiedAccountsCount}
                          unidentifiedFromRevenueCount={unidentifiedFromRevenueCount}
                          unidentifiedFromProductCount={unidentifiedFromProductCount}
                          selectedSource={selectedSources[0] as 'stripe' | 'api' | 'csv' | null || null}
                          accountIdentifier="group_id"
                          filteredIssueIds={['missing-revenue-data']}
                          hideExportImport={true}
                          showRedirectLink={true}
                          redirectLink={`/analyze/diagnostics-report/revenue-product-correlation?subIssueId=missing-revenue-data`}
                          redirectLinkText="View detailed resolution steps"
                          displayIndex={(selectedSources.includes('stripe') || selectedSources.includes('chargebee')) ? 3 : 2}
                        />

                        {/* Transactions without invoices */}
                        <RevenueMissingDataCards
                          expandedIssueId={expandedRevenueMissingIssueId}
                          onToggleIssue={setExpandedRevenueMissingIssueId}
                          transactionsWithoutInvoiceCount={transactionsWithoutInvoiceCount}
                          filteredIssueIds={['transactions-without-invoices']}
                          hideExportImport={true}
                          showRedirectLink={true}
                          redirectLink={`/analyze/diagnostics-report/revenue-missing-data?subIssueId=transactions-without-invoices`}
                          redirectLinkText="View detailed resolution steps"
                          displayIndex={(selectedSources.includes('stripe') || selectedSources.includes('chargebee')) ? 4 : 3}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {true && (
      <div className="bg-white w-[84%] mx-auto fixed bottom-0 flex justify-end items-center py-4 px-8 z-10">
        <div className="flex space-x-4">
          {saveError && <span className="text-red-600 mr-4">{saveError}</span>}
          {!isDemoMode && (
          <button
            id="detect_demo_active"
            className="px-3 py-3 rounded-lg font-medium transition-transform duration-200 text-blue-800 hover:bg-white text-sm hover:scale-[1.02] underline underline-offset-4"
            onClick={() => SkipAndTryDemo()}
          >
            <span className="flex items-center space-x-2">
              Skip setup, try demo mode <ArrowRight className="w-5 h-5 mr-1" />
            </span>
          </button>
          )}
          {currentStep === 5 ? (
            (() => {
              const live = step5LiveGoals;
              const anyEnabled = live ? hasAnySetGoalEnabled(live) : false;
              const canSubmitGoals = live ? isSetGoalsStepCompleted(live) : false;
              const saveThenNavDisabled =
                step5NavBusy || (anyEnabled && !canSubmitGoals);
              return (
            <>
              <button
                type="button"
                onClick={() => void handleStep5GoToIntegrations()}
                disabled={saveThenNavDisabled}
                className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold bg-white border-2 border-primaryColor text-primaryColor transition-colors ${
                  saveThenNavDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              >
                <span>
                  {step5NavBusy
                    ? "Saving…"
                    : anyEnabled
                      ? "Save goals & go to Integrations"
                      : "Go to Integrations"}
                </span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => void handleStep5GoToDashboard()}
                disabled={saveThenNavDisabled}
                className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold text-white transition-colors shadow-lg hover:shadow-xl ${
                  saveThenNavDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-primaryColor hover:bg-primaryColor"
                }`}
              >
                <span>
                  {step5NavBusy
                    ? "Saving…"
                    : anyEnabled
                      ? "Save goals & go to Dashboard"
                      : "Go to Dashboard"}
                </span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </>
              );
            })()
          ) : (
          <button
            onClick={() => onStepComplete()}
            disabled={!isCurrentStepCompleted() || processing || (currentStep === 2 && activeSourceTab === 'chargebee' && sourceConfigurations.chargebee?.isSetupIncomplete)}
            className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold text-white transition-colors shadow-lg hover:shadow-xl ${!isCurrentStepCompleted() || processing || (currentStep === 2 && activeSourceTab === 'chargebee' && sourceConfigurations.chargebee?.isSetupIncomplete)
              ? 'bg-gray-400 cursor-not-allowed opacity-60'
              : 'bg-primaryColor hover:bg-primaryColor'
              }`}
          >
            <span>
              {processing
                ? "Processing..."
                : currentStep === steps.length
                  ? "Go to Dashboard"
                  : currentStep === 2 && !checkAtLeastOneSourceCompleted()
                    ? "Complete Source Setup"
                    : "Next Step"}
            </span>
            <ArrowRight className="w-5 h-5" />
          </button>
          )}
        </div>
      </div>
      )}

      <NotificationPreferencesModal
        isOpen={revenueNotificationPrefs.isOpen}
        onClose={revenueNotificationPrefs.close}
        featureName="Revenue Intelligence"
        notifications={[{ key: REVENUE_NOTIFICATION_KEY }]}
      />

      {selectedTeam && showInviteModal && (
        <InviteTeamModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          team={selectedTeam ?? undefined}
          onInvite={() => { }}
        />
      )}

      {/* Setup Guide Modal - Stripe or Chargebee based on setupGuideSource / selectedSources */}
      {isSetupGuideOpen && (() => {
        const guideSource = setupGuideSource ?? (selectedSources?.includes('chargebee') ? 'chargebee' : 'stripe');
        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50"
            onClick={() => { setIsSetupGuideOpen(false); setSetupGuideSource(null); }}
            role="presentation"
            aria-hidden="false"
          >
            <div
              className="mt-10 h-[calc(100%-2.5rem)] w-[600px] bg-white shadow-xl overflow-y-auto flex flex-col"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Setup guide"
            >
              <div className="p-6 flex-1 overflow-y-auto">
                {guideSource === 'chargebee' ? (
                  <ChargebeeSetupGuide step3Ref={setupGuideStep3Ref} setExpanded={setExpanded} expanded={expanded} onClose={() => { setIsSetupGuideOpen(false); setSetupGuideSource(null); }} />
                ) : (
                  <SetupGuide step3Ref={setupGuideStep3Ref} setExpanded={setExpanded} expanded={expanded} onClose={() => { setIsSetupGuideOpen(false); setSetupGuideSource(null); }} />
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Health Status Guide */}
      <SetupHealthStatusGuide
        isOpen={isHealthStatusGuideOpen}
        onClose={() => setIsHealthStatusGuideOpen(false)}
        setupContext="revenue-analytics"
        selectedSource={selectedSources[0] as 'stripe' | 'api' | 'csv' | null || null}
        onNavigateToConfigureStep={() => {
          setExpandedAccountIdentifierSection(true);
          setTimeout(() => {
            const src = selectedSources[0];
            const sectionId =
              src === "chargebee" ? "missing-account-identifier-chargebee" : "missing-account-identifier-stripe";
            const element = document.getElementById(sectionId);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 100);
        }}
        onNavigateToIssueCard={(issueId) => {
          if (issueId === 'id-missing-accounts') {
            setExpandedIssueId('id-missing-accounts');
          } else if (issueId === 'missing-revenue-data') {
            setExpandedIssueId('missing-revenue-data');
          }
          setTimeout(() => {
            const element = document.getElementById(`issue-card-${issueId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }}
      />
    </div>
  );
}

export default RevenueOptimization;
